import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  BookOpen,
  Check,
  ImageIcon,
  Layers,
  Loader2,
  Package,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Upload,
  X,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { HelpHint } from '@/components/ui/help-hint'
import { AssetGridPicker } from '@/components/create/AssetGridPicker'
import { apiFetch, apiJson } from '@/lib/api'
import type { Asset, AssetFolder, VideoModel } from '@/types'

// ─── Shared types ─────────────────────────────────────────────────────────────

interface AnalyzeResponse {
  description: string
  features: string[]
  suggested_prompt: string
}

interface GenerationResult {
  local_path: string
  variation_index: number
  error?: string
}

interface GenerateResponse {
  results: GenerationResult[]
  total_generated: number
  total_failed: number
}

interface CompositeResponse {
  asset_ids: number[]
  folder_id: number
  folder_name: string
  total_generated: number
  total_failed: number
  results: GenerationResult[]
}

// ─── Shared constants ─────────────────────────────────────────────────────────

const VARIATION_OPTIONS = [1, 2, 3, 4, 5] as const

// UI shape used throughout this page. Built from the /api/v1/models catalog
// at runtime so new models (e.g. GPT-5.4 Image 2 from Phase 5) appear
// without a frontend redeploy.
interface ImageUiModel {
  id: string
  name: string
  label: string
  price: string
  priceNum: number
  aspectRatios: string[]
  sizes: string[]
  defaultSize: string
}

// Sensible fallback that mirrors the prior hardcoded list so the page is
// still usable if /api/v1/models fails or is slow.
const FALLBACK_IMAGE_MODELS: ImageUiModel[] = [
  {
    id: 'gemini-3-pro-image-preview',
    name: 'Nano Banana Pro (Google)',
    label: 'Best',
    price: '$0.04/img',
    priceNum: 0.04,
    aspectRatios: ['1:1', '16:9', '9:16', '3:4', '4:3'],
    sizes: ['512', '1K', '2K', '4K'],
    defaultSize: '2K',
  },
]

function _catalogToUiImageModel(m: VideoModel): ImageUiModel {
  // Catalog → UI shape. Sizes default to the existing pill set when the
  // catalog entry doesn't specify (e.g. OpenAI image models which use AR
  // only — we keep the pills visible but the backend ignores them).
  const fallbackSizes = ['512', '1K', '2K', '4K']
  const sizes = (m.sizes && m.sizes.length > 0 ? m.sizes : fallbackSizes) as string[]
  const isPro = m.id.toLowerCase().includes('pro') || m.id.includes('image-2')
  const label = m.id.toLowerCase().includes('flash') ? 'Fast' : (isPro ? 'Best' : 'Std')
  const priceNum = typeof m.price_per_image === 'number' ? m.price_per_image : 0.04
  return {
    id: m.id,
    name: m.name,
    label,
    price: priceNum > 0 ? `$${priceNum.toFixed(priceNum < 0.1 ? 3 : 2)}/img` : 'see usage',
    priceNum,
    aspectRatios: (m.aspect_ratios && m.aspect_ratios.length > 0
      ? m.aspect_ratios
      : ['1:1', '9:16', '16:9', '3:4', '4:3']) as string[],
    sizes,
    defaultSize: isPro ? '2K' : '1K',
  }
}

/** Fetches the image-model catalog once per mount with fallback. */
function useImageModels(): ImageUiModel[] {
  const [models, setModels] = React.useState<ImageUiModel[]>(FALLBACK_IMAGE_MODELS)
  React.useEffect(() => {
    apiJson<{ items: VideoModel[] }>('/api/v1/models')
      .then(({ items }) => {
        const imgs = items.filter(
          (m) => m.mode === 'text-to-image' || m.mode === 'image-to-image',
        )
        if (imgs.length > 0) setModels(imgs.map(_catalogToUiImageModel))
      })
      .catch(() => {
        // keep fallback
      })
  }, [])
  return models
}

const COMPOSITE_ASPECT_RATIOS = ['1:1', '9:16', '16:9'] as const
const COMPOSITE_QUALITY_OPTIONS = ['512', '1K', '2K'] as const

type ModelId = string

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Pill({
  label,
  selected,
  onClick,
  disabled,
  flex,
}: {
  label: string
  selected: boolean
  onClick: () => void
  disabled?: boolean
  flex?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
        flex ? 'flex-1 text-center' : '',
        selected
          ? 'border-foreground bg-foreground/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-muted',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label}
    </button>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
      {children}
    </p>
  )
}

function StepNumber({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-foreground/10 text-foreground text-[10px] font-bold mr-2 shrink-0">
      {n}
    </span>
  )
}

function ResultCard({
  result,
  selected,
  onToggle,
  saved,
  label,
}: {
  result: GenerationResult
  selected: boolean
  onToggle: () => void
  saved: boolean
  label: string
}) {
  const [imgError, setImgError] = React.useState(false)
  const hasError = Boolean(result.error)
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={hasError || saved}
      aria-pressed={selected}
      aria-label={
        hasError
          ? `${label} ${result.variation_index + 1} failed`
          : `${label} ${result.variation_index + 1}${selected ? ', selected' : ''}`
      }
      className={[
        'relative group rounded-lg overflow-hidden border-2 transition-all text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        hasError
          ? 'border-destructive/40 opacity-50 cursor-not-allowed'
          : selected
          ? 'ring-2 ring-foreground border-foreground'
          : 'border-transparent hover:border-border',
      ].join(' ')}
    >
      <div className="aspect-square bg-muted relative">
        {!imgError && !hasError ? (
          <img
            src={result.local_path ? `/uploads/${result.local_path}` : ''}
            alt={`${label} ${result.variation_index + 1}`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2">
            <ImageIcon className="h-6 w-6 text-muted-foreground opacity-40" />
            {hasError && (
              <span className="text-[10px] text-destructive text-center leading-tight">
                {result.error}
              </span>
            )}
          </div>
        )}
      </div>
      {!hasError && (
        <div
          className={[
            'absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center transition-colors',
            selected
              ? 'bg-foreground text-background'
              : 'bg-black/40 text-white/60 group-hover:bg-black/60',
          ].join(' ')}
          aria-hidden="true"
        >
          {selected && <Check className="h-4 w-4" />}
        </div>
      )}
      <div className="p-2">
        <span className="text-xs font-medium">v{result.variation_index + 1}</span>
      </div>
    </button>
  )
}

// ─── Hierarchical folder options ─────────────────────────────────────────────

function buildFolderOptions(folders: AssetFolder[]): Array<{ id: number; label: string; depth: number }> {
  // Build parent→children map
  const roots = folders.filter((f) => !f.parent_folder_id)
  const childrenOf = (parentId: number) => folders.filter((f) => f.parent_folder_id === parentId)

  const result: Array<{ id: number; label: string; depth: number }> = []

  function walk(folder: AssetFolder, depth: number) {
    result.push({ id: folder.id, label: folder.name, depth })
    for (const child of childrenOf(folder.id)) {
      walk(child, depth + 1)
    }
  }

  for (const root of roots) {
    walk(root, 0)
  }
  return result
}

function SaveSection({
  folders,
  successfulResults,
  saving,
  saved,
  selectedCount,
  savedCount,
  onSave,
  onReset,
}: {
  folders: AssetFolder[]
  successfulResults: GenerationResult[]
  saving: boolean
  saved: boolean
  selectedCount: number
  savedCount: number
  onSave: (folderId: number | null, newFolderName: string) => Promise<void>
  onReset: () => void
}) {
  const [savingFolderId, setSavingFolderId] = React.useState('')
  const [newFolderName, setNewFolderName] = React.useState('')
  const canSave = selectedCount > 0 && (savingFolderId !== '' || newFolderName.trim() !== '') && !saving
  const folderOptions = React.useMemo(() => buildFolderOptions(folders), [folders])

  if (saved) {
    return (
      <div className="flex items-center gap-2 text-sm font-medium" role="status" aria-live="polite">
        <Check className="h-5 w-5 shrink-0" aria-hidden="true" />
        {savedCount} image{savedCount !== 1 ? 's' : ''} saved to library.
        <Button variant="ghost" size="sm" onClick={onReset} className="ml-2">
          Start Over
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="save-folder-select">Add to existing folder</Label>
          <select
            id="save-folder-select"
            value={savingFolderId}
            onChange={(e) => { setSavingFolderId(e.target.value); if (e.target.value) setNewFolderName('') }}
            disabled={saving || newFolderName.trim() !== ''}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">Choose a folder…</option>
            {folderOptions.map((f) => (
              <option key={f.id} value={String(f.id)}>
                {f.depth > 0 ? '\u00a0\u00a0'.repeat(f.depth) + '↳ ' : ''}{f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="save-folder-new">Or create new folder</Label>
          <Input
            id="save-folder-new"
            type="text"
            value={newFolderName}
            onChange={(e) => { setNewFolderName(e.target.value); if (e.target.value) setSavingFolderId('') }}
            disabled={saving}
            placeholder="e.g. Summer Campaign"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          onClick={() => { void onSave(savingFolderId ? Number(savingFolderId) : null, newFolderName) }}
          disabled={!canSave}
          aria-busy={saving}
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-1" />Saving…</>
          ) : (
            <><Save className="h-4 w-4 mr-1" />Save {selectedCount} to Library</>
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={onReset} disabled={saving}>
          Start Over
        </Button>
      </div>
      {selectedCount === 0 && successfulResults.length > 0 && (
        <p className="text-xs text-muted-foreground" role="status">
          Select at least one image above before saving.
        </p>
      )}
    </div>
  )
}

// ─── Derive product folder IDs ────────────────────────────────────────────────

function getProductFolderIds(folders: AssetFolder[]): number[] {
  const productsRoot = folders.find((f) => f.name === 'Products' && !f.parent_folder_id)
  if (!productsRoot) return []
  const level1 = folders.filter((f) => f.parent_folder_id === productsRoot.id)
  const level2 = folders.filter((f) =>
    level1.some((l1) => l1.id === f.parent_folder_id)
  )
  return [productsRoot.id, ...level1.map((f) => f.id), ...level2.map((f) => f.id)]
}

// ─── Feature chips (for Avatar tab) ──────────────────────────────────────────

function FeatureChips({
  features,
  onRemove,
  onAdd,
  disabled,
}: {
  features: string[]
  onRemove: (index: number) => void
  onAdd: (value: string) => void
  disabled?: boolean
}) {
  const [draft, setDraft] = React.useState('')
  const commit = () => {
    const t = draft.trim()
    if (!t) return
    onAdd(t)
    setDraft('')
  }
  return (
    <div className="space-y-2">
      <div
        className="flex flex-wrap gap-1.5 min-h-[36px] p-2 rounded-md border border-input bg-background"
        role="list"
        aria-label="Feature tags"
      >
        {features.map((feat, i) => (
          <Badge key={i} variant="secondary" className="flex items-center gap-1 pr-1" role="listitem">
            {feat}
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                aria-label={`Remove "${feat}"`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        {features.length === 0 && (
          <span className="text-xs text-muted-foreground self-center px-1">No features yet</span>
        )}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit() } }}
            placeholder="Add a feature tag…"
            className="text-sm h-8"
            aria-label="New feature tag"
          />
          <Button type="button" variant="outline" size="sm" onClick={commit} className="h-8 px-2" aria-label="Add tag">
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── CompactUpload ─────────────────────────────────────────────────────────────

function CompactUpload({
  onFile,
  label = 'Upload new image',
}: {
  onFile: (file: File) => void
  label?: string
}) {
  const ref = React.useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
      >
        <Upload className="h-3.5 w-3.5" />
        {label}
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
          e.target.value = ''
        }}
      />
    </>
  )
}

// ─── Avatar tab ───────────────────────────────────────────────────────────────

function AvatarTab({
  allAssets,
  allFolders,
}: {
  allAssets: Asset[]
  allFolders: AssetFolder[]
}) {
  const { toast } = useToast()

  // Image-model catalog (fetched from /api/v1/models on mount, falls back to
  // a hardcoded Nano Banana entry while in flight). Phase 5+ entries like
  // "GPT-5.4 Image 2 (OpenAI)" appear here automatically.
  const imageModels = useImageModels()

  // Upload
  const [imageFile, setImageFile] = React.useState<File | null>(null)
  const [imagePreview, setImagePreview] = React.useState<string | null>(null)
  const [dragOver, setDragOver] = React.useState(false)
  const isDragging = React.useRef(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Library selection (when not uploading)
  const [librarySelectedId, setLibrarySelectedId] = React.useState<number | null>(null)

  // Steps refs for scroll
  const step3Ref = React.useRef<HTMLDivElement>(null)
  const step4Ref = React.useRef<HTMLDivElement>(null)
  const step5Ref = React.useRef<HTMLDivElement>(null)
  const resultsRef = React.useRef<HTMLDivElement>(null)

  // Analysis
  const [analyzing, setAnalyzing] = React.useState(false)
  const [analysisResult, setAnalysisResult] = React.useState<AnalyzeResponse | null>(null)

  // Form fields
  const [avatarName, setAvatarName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [features, setFeatures] = React.useState<string[]>([])
  const [prompt, setPrompt] = React.useState('')
  const [customPrompt, setCustomPrompt] = React.useState('')
  const [tweaks, setTweaks] = React.useState('')

  // Settings
  const [numVariations, setNumVariations] = React.useState<number>(3)
  const [model, setModel] = React.useState<ModelId>(imageModels[0].id)
  const [aspectRatio, setAspectRatio] = React.useState<string>('1:1')
  const [imageSize, setImageSize] = React.useState<string>(imageModels[0].defaultSize)

  // Generation
  const [generating, setGenerating] = React.useState(false)
  const [generationResults, setGenerationResults] = React.useState<GenerationResult[] | null>(null)
  const [selectedIndices, setSelectedIndices] = React.useState<Set<number>>(new Set())
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const selectedModel = imageModels.find((m) => m.id === model) ?? imageModels[0]
  const hasAnalysis = analysisResult !== null
  const hasResults = generationResults !== null && generationResults.length > 0
  const successfulResults = React.useMemo(
    () => (generationResults ?? []).filter((r) => !r.error),
    [generationResults]
  )
  const selectedCount = selectedIndices.size
  const estimatedCost = (selectedModel.priceNum * numVariations).toFixed(2)
  const canGenerate =
    avatarName.trim().length > 0 &&
    (prompt.trim().length > 0 || customPrompt.trim().length > 0) &&
    !generating

  const analyzeFile = React.useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        toast({ title: 'Invalid file', description: 'Please upload an image file.', variant: 'destructive' })
        return
      }
      const url = URL.createObjectURL(file)
      setImageFile(file)
      setImagePreview(url)
      setLibrarySelectedId(null)
      setAnalyzing(true)
      try {
        const fd = new FormData()
        fd.append('image', file)
        const res = await apiFetch('/api/v1/avatars/analyze', { method: 'POST', body: fd })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error((err as { detail?: string }).detail || `Analysis failed (${res.status})`)
        }
        const data: AnalyzeResponse = await res.json()
        setAnalysisResult(data)
        setDescription(data.description)
        setFeatures(data.features)
        setPrompt(data.suggested_prompt)
        setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
      } catch (err) {
        toast({ title: 'Analysis failed', description: (err as Error).message, variant: 'destructive' })
      } finally {
        setAnalyzing(false)
      }
    },
    [toast]
  )

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    isDragging.current = false
    const file = e.dataTransfer.files?.[0]
    if (file) void analyzeFile(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!isDragging.current) { isDragging.current = true; setDragOver(true) }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      isDragging.current = false
      setDragOver(false)
    }
  }

  const handleLibrarySelect = (asset: Asset) => {
    setLibrarySelectedId(asset.id)
    setImagePreview(`/uploads/${asset.file_path}`)
    setImageFile(null)
    setAnalyzing(true)
    fetch(`/uploads/${asset.file_path}`)
      .then((r) => r.blob())
      .then((blob) => {
        const fd = new FormData()
        fd.append('image', blob, asset.filename)
        return apiFetch('/api/v1/avatars/analyze', { method: 'POST', body: fd })
      })
      .then((r) => r.json())
      .then((data: AnalyzeResponse) => {
        if (data.description) {
          setAnalysisResult(data)
          setDescription(data.description)
          setFeatures(data.features)
          setPrompt(data.suggested_prompt)
          setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
        }
      })
      .catch(() => {})
      .finally(() => setAnalyzing(false))
  }

  const handleSwitchModel = (id: ModelId) => {
    const m = imageModels.find((x) => x.id === id)
    if (!m) return
    setModel(id)
    setImageSize(m.defaultSize)
  }

  const handleGenerate = async () => {
    if (!canGenerate) return
    setGenerating(true)
    setGenerationResults(null)
    setSelectedIndices(new Set())
    setSaved(false)
    try {
      const fd = new FormData()
      fd.append('name', avatarName.trim())
      const tweaksSuffix = tweaks.trim() ? `. Additional changes: ${tweaks.trim()}` : ''
      const finalPrompt = [prompt.trim(), customPrompt.trim()].filter(Boolean).join('\n\n') + tweaksSuffix
      fd.append('prompt', finalPrompt)
      fd.append('num_variations', String(numVariations))
      fd.append('model', model)
      fd.append('aspect_ratio', aspectRatio)
      fd.append('image_size', imageSize)
      if (imageFile) fd.append('image', imageFile)
      const res = await apiFetch('/api/v1/avatars/generate', { method: 'POST', body: fd })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail || `Generation failed (${res.status})`)
      }
      const data: GenerateResponse = await res.json()
      setGenerationResults(data.results)
      setSelectedIndices(new Set(data.results.filter((r) => !r.error).map((r) => r.variation_index)))
      if (data.total_failed > 0) {
        toast({ title: `${data.total_generated} generated, ${data.total_failed} failed`, variant: 'destructive' })
      } else {
        toast({ title: `${data.total_generated} avatar${data.total_generated !== 1 ? 's' : ''} generated` })
      }
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    } catch (err) {
      toast({ title: 'Generation failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setGenerating(false)
    }
  }

  const handleSave = async (folderId: number | null, newFolderName: string) => {
    if (!generationResults) return
    setSaving(true)
    const selected = generationResults.filter((r) => selectedIndices.has(r.variation_index))
    try {
      let resolvedFolderId = folderId ?? 0
      if (newFolderName.trim() && !folderId) {
        const res = await apiJson<{ id: number }>('/api/v1/folders', {
          method: 'POST',
          body: JSON.stringify({ name: newFolderName.trim(), folder_type: 'general' }),
        })
        resolvedFolderId = res.id
      }
      await apiJson('/api/v1/avatars/save', {
        method: 'POST',
        body: JSON.stringify({
          name: avatarName.trim(),
          description: description.trim(),
          features,
          folder_id: resolvedFolderId,
          image_type: 'avatar',
          selected: selected.map((r) => ({ local_path: r.local_path, variation_index: r.variation_index })),
        }),
      })
      setSaved(true)
      toast({ title: `${selected.length} avatar${selected.length !== 1 ? 's' : ''} saved` })
    } catch (err) {
      toast({ title: 'Save failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (imagePreview && imageFile) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
    setLibrarySelectedId(null)
    setAnalyzing(false)
    setAnalysisResult(null)
    setAvatarName('')
    setDescription('')
    setFeatures([])
    setPrompt('')
    setCustomPrompt('')
    setTweaks('')
    setNumVariations(3)
    setModel(imageModels[0].id)
    setAspectRatio('1:1')
    setImageSize(imageModels[0].defaultSize)
    setGenerating(false)
    setGenerationResults(null)
    setSelectedIndices(new Set())
    setSaving(false)
    setSaved(false)
  }

  React.useEffect(() => {
    return () => { if (imagePreview && imageFile) URL.revokeObjectURL(imagePreview) }
  }, [imagePreview, imageFile])

  return (
    <div className="space-y-0">

      {/* ── STEP 1: Upload ─────────────────────────────────────────────────── */}
      <div className="space-y-4 py-6">
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={1} />
          Upload reference photo
        </p>

        <div className="flex items-start gap-4 flex-wrap">
          {imagePreview ? (
            <div className="relative h-24 w-24 shrink-0">
              <img
                src={imagePreview}
                alt="Reference"
                className="h-24 w-24 rounded-md object-cover border border-border"
              />
              <button
                type="button"
                onClick={() => {
                  if (imageFile) URL.revokeObjectURL(imagePreview)
                  setImageFile(null)
                  setImagePreview(null)
                  setLibrarySelectedId(null)
                  setAnalysisResult(null)
                }}
                className="absolute -top-1.5 -right-1.5 rounded-full bg-background border border-border p-0.5 hover:bg-muted"
                aria-label="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              {analyzing && (
                <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/60">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              aria-label="Upload reference image"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click() } }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={[
                'h-24 w-24 flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                dragOver ? 'border-foreground bg-foreground/5' : 'border-border hover:border-foreground/40',
              ].join(' ')}
            >
              <Upload className="h-6 w-6 text-muted-foreground/50" />
              <span className="text-[10px] text-muted-foreground text-center leading-tight px-1">Drop or click</span>
            </div>
          )}
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              Upload a reference photo, or select from your avatar library below.
            </p>
            {!imagePreview && (
              <CompactUpload onFile={(f) => { void analyzeFile(f) }} label="Upload reference photo" />
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void analyzeFile(f); e.target.value = '' }}
        />
      </div>

      <Separator className="my-0" />

      {/* ── STEP 2: Select from library ────────────────────────────────────── */}
      <div className="space-y-4 py-6">
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={2} />
          Or select from avatar library
        </p>
        <AssetGridPicker
          label="Your avatars"
          assets={allAssets}
          folders={allFolders}
          selectedId={librarySelectedId}
          onSelect={handleLibrarySelect}
          filterSceneType="model"
        />
      </div>

      <Separator className="my-0" />

      {/* ── STEP 3: Analysis + edit fields ─────────────────────────────────── */}
      <div className="space-y-4 py-6" ref={step3Ref}>
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={3} />
          Review analysis &amp; set details
        </p>

        {!imagePreview && !analyzing && (
          <p className="text-sm text-muted-foreground">Upload or select an image above to auto-populate.</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="avatar-name">
                Avatar name <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              {analyzing ? <Skeleton className="h-9 w-full" /> : (
                <Input id="avatar-name" value={avatarName} onChange={(e) => setAvatarName(e.target.value)} placeholder="e.g. Brand Hero" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="avatar-description">Description</Label>
              {analyzing ? <Skeleton className="h-20 w-full" /> : (
                <Textarea id="avatar-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the avatar's visual style…" rows={3} className="resize-none" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Features</Label>
              {analyzing ? (
                <div className="flex flex-wrap gap-1.5">{[70, 90, 60, 80].map((w) => <Skeleton key={w} className="h-6 rounded-full" style={{ width: w }} />)}</div>
              ) : (
                <FeatureChips features={features} onRemove={(i) => setFeatures((p) => p.filter((_, idx) => idx !== i))} onAdd={(v) => setFeatures((p) => [...p, v])} />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="avatar-prompt">
                Generation prompt <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              {analyzing ? <Skeleton className="h-24 w-full" /> : (
                <Textarea id="avatar-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe what to generate…" rows={4} className="resize-none font-mono text-xs" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="avatar-custom-prompt">Additional instructions <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea id="avatar-custom-prompt" value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="e.g. cinematic lighting, white background…" rows={2} className="resize-none text-sm" disabled={analyzing} />
            </div>
            {hasAnalysis && !analyzing && (
              <div className="space-y-1.5">
                <Label htmlFor="avatar-tweaks">Describe changes <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea id="avatar-tweaks" value={tweaks} onChange={(e) => setTweaks(e.target.value)} placeholder="e.g. add sunglasses, darker hair, more professional…" rows={2} className="resize-none text-sm" disabled={generating} />
              </div>
            )}
          </div>
        </div>
      </div>

      <Separator className="my-0" />

      {/* ── STEP 4: Settings ───────────────────────────────────────────────── */}
      <div className="space-y-5 py-6" ref={step4Ref}>
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={4} />
          Settings
        </p>

        <div>
          <SectionLabel>Model</SectionLabel>
          <div className="flex gap-3">
            {imageModels.map((m) => (
              <button key={m.id} type="button" onClick={() => handleSwitchModel(m.id)} disabled={generating}
                className={['flex-1 rounded-lg border px-4 py-3 text-left transition-colors', model === m.id ? 'border-foreground bg-foreground/10' : 'border-border hover:bg-muted', generating ? 'opacity-50 cursor-not-allowed' : ''].join(' ')}>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{m.label} &middot; {m.price}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <SectionLabel>Aspect Ratio</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {selectedModel.aspectRatios.map((r) => (
                <Pill key={r} label={r} selected={aspectRatio === r} onClick={() => setAspectRatio(r)} disabled={generating} />
              ))}
            </div>
          </div>
          <div>
            <SectionLabel>Quality</SectionLabel>
            <div className="flex gap-2">
              {selectedModel.sizes.map((s) => (
                <Pill key={s} label={s} selected={imageSize === s} onClick={() => setImageSize(s)} disabled={generating} flex />
              ))}
            </div>
          </div>
          <div>
            <SectionLabel>Variations</SectionLabel>
            <div className="flex gap-2">
              {VARIATION_OPTIONS.map((n) => (
                <Pill key={n} label={String(n)} selected={numVariations === n} onClick={() => setNumVariations(n)} disabled={generating} flex />
              ))}
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Estimated cost: {selectedModel.price} &times; {numVariations} = ~${estimatedCost}
        </p>
      </div>

      <Separator className="my-0" />

      {/* ── STEP 5: Generate ───────────────────────────────────────────────── */}
      <div className="py-6" ref={step5Ref}>
        <div className="flex gap-3">
          <button onClick={() => { void handleGenerate() }} disabled={!canGenerate} aria-busy={generating}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg border bg-foreground text-background px-6 py-3 font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors">
            {generating ? (
              <><Loader2 className="h-5 w-5 animate-spin" />Generating {numVariations} avatar{numVariations > 1 ? 's' : ''}…</>
            ) : (
              <><Sparkles className="h-5 w-5" />Generate {numVariations} Avatar{numVariations > 1 ? 's' : ''}</>
            )}
          </button>
          <button onClick={handleReset} disabled={generating} className="px-4 py-3 rounded-lg border hover:bg-muted transition-colors disabled:opacity-50 text-sm font-medium">Reset</button>
        </div>
      </div>

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {(generating || hasResults) && (
        <>
          <Separator className="my-0" />
          <div className="space-y-5 py-6" ref={resultsRef}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="flex items-center text-sm font-semibold"><StepNumber n={6} />Select to save</p>
              {hasResults && !generating && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">{selectedCount} of {successfulResults.length} selected</span>
                  <button onClick={() => setSelectedIndices(new Set(successfulResults.map((r) => r.variation_index)))} disabled={selectedCount === successfulResults.length} className="hover:underline disabled:opacity-40">All</button>
                  <button onClick={() => setSelectedIndices(new Set())} disabled={selectedCount === 0} className="text-muted-foreground hover:underline disabled:opacity-40">None</button>
                </div>
              )}
            </div>
            {generating && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" aria-live="polite">
                {Array.from({ length: numVariations }).map((_, i) => <div key={i} className="rounded-lg overflow-hidden border"><Skeleton className="aspect-square w-full" /></div>)}
              </div>
            )}
            {hasResults && !generating && generationResults && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" aria-live="polite">
                {generationResults.map((r) => (
                  <ResultCard key={r.variation_index} result={r} label="Avatar" selected={selectedIndices.has(r.variation_index)}
                    onToggle={() => setSelectedIndices((prev) => { const next = new Set(prev); if (next.has(r.variation_index)) next.delete(r.variation_index); else next.add(r.variation_index); return next })}
                    saved={saved} />
                ))}
              </div>
            )}
          </div>

          <Separator className="my-0" />
          <div className="py-6">
            <p className="flex items-center text-sm font-semibold mb-4"><StepNumber n={7} />Save to library</p>
            <SaveSection
              folders={allFolders}
              successfulResults={successfulResults}
              saving={saving}
              saved={saved}
              selectedCount={selectedCount}
              savedCount={selectedCount}
              onSave={handleSave}
              onReset={handleReset}
            />
          </div>
        </>
      )}
    </div>
  )
}

// ─── Product tab ──────────────────────────────────────────────────────────────

function ProductTab({
  allAssets,
  allFolders,
}: {
  allAssets: Asset[]
  allFolders: AssetFolder[]
}) {
  const { toast } = useToast()

  // Same catalog hook as the avatar flow above.
  const imageModels = useImageModels()

  const productFolderIds = React.useMemo(() => getProductFolderIds(allFolders), [allFolders])

  // Selection
  const [selectedAssetId, setSelectedAssetId] = React.useState<number | null>(null)
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null)
  const [uploadedPreview, setUploadedPreview] = React.useState<string | null>(null)

  // Analysis
  const [analyzing, setAnalyzing] = React.useState(false)
  const [analysisResult, setAnalysisResult] = React.useState<AnalyzeResponse | null>(null)

  // Refs
  const step2Ref = React.useRef<HTMLDivElement>(null)
  const step3Ref = React.useRef<HTMLDivElement>(null)
  const step4Ref = React.useRef<HTMLDivElement>(null)
  const resultsRef = React.useRef<HTMLDivElement>(null)

  // Variation prompt
  const [variationText, setVariationText] = React.useState('')

  // Settings
  const [numVariations, setNumVariations] = React.useState<number>(2)
  const [model, setModel] = React.useState<ModelId>(imageModels[0].id)
  const [aspectRatio, setAspectRatio] = React.useState<string>('1:1')
  const [imageSize, setImageSize] = React.useState<string>(imageModels[0].defaultSize)

  // Generation
  const [generating, setGenerating] = React.useState(false)
  const [generationResults, setGenerationResults] = React.useState<GenerationResult[] | null>(null)
  const [selectedIndices, setSelectedIndices] = React.useState<Set<number>>(new Set())
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const selectedModel = imageModels.find((m) => m.id === model) ?? imageModels[0]
  const hasAnalysis = analysisResult !== null
  const hasResults = generationResults !== null && generationResults.length > 0
  const successfulResults = React.useMemo(() => (generationResults ?? []).filter((r) => !r.error), [generationResults])
  const selectedCount = selectedIndices.size
  const estimatedCost = (selectedModel.priceNum * numVariations).toFixed(2)

  const activeImageSource = selectedAssetId !== null || uploadedPreview !== null
  const canGenerate = activeImageSource && !generating

  const analyzeFromUrl = React.useCallback(
    async (url: string, filename: string) => {
      setAnalyzing(true)
      setAnalysisResult(null)
      try {
        const blob = await fetch(url).then((r) => r.blob())
        const fd = new FormData()
        fd.append('image', blob, filename)
        const res = await apiFetch('/api/v1/avatars/analyze', { method: 'POST', body: fd })
        if (!res.ok) throw new Error(`Analysis failed (${res.status})`)
        const data: AnalyzeResponse = await res.json()
        setAnalysisResult(data)
        setVariationText(data.suggested_prompt || '')
        setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
      } catch {
        toast({ title: 'Analysis failed', description: 'Could not analyze the image.', variant: 'destructive' })
      } finally {
        setAnalyzing(false)
      }
    },
    [toast]
  )

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAssetId(asset.id)
    if (uploadedPreview && uploadedFile) URL.revokeObjectURL(uploadedPreview)
    setUploadedFile(null)
    setUploadedPreview(null)
    void analyzeFromUrl(`/uploads/${asset.file_path}`, asset.filename)
  }

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please upload an image file.', variant: 'destructive' })
      return
    }
    const url = URL.createObjectURL(file)
    setUploadedFile(file)
    setUploadedPreview(url)
    setSelectedAssetId(null)
    void analyzeFromUrl(url, file.name)
  }

  const handleSwitchModel = (id: ModelId) => {
    const m = imageModels.find((x) => x.id === id)
    if (!m) return
    setModel(id)
    setImageSize(m.defaultSize)
  }

  const handleGenerate = async () => {
    if (!canGenerate) return
    setGenerating(true)
    setGenerationResults(null)
    setSelectedIndices(new Set())
    setSaved(false)
    try {
      const fd = new FormData()
      const baseDesc = analysisResult?.description ?? ''
      const finalPrompt = [baseDesc, variationText.trim()].filter(Boolean).join('. ')
      fd.append('name', 'Product variation')
      fd.append('prompt', finalPrompt)
      fd.append('num_variations', String(numVariations))
      fd.append('model', model)
      fd.append('aspect_ratio', aspectRatio)
      fd.append('image_size', imageSize)

      if (uploadedFile) {
        fd.append('image', uploadedFile)
      } else if (selectedAssetId !== null) {
        const asset = allAssets.find((a) => a.id === selectedAssetId)
        if (asset) {
          const blob = await fetch(`/uploads/${asset.file_path}`).then((r) => r.blob())
          fd.append('image', blob, asset.filename)
        }
      }

      const res = await apiFetch('/api/v1/avatars/generate', { method: 'POST', body: fd })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail || `Generation failed (${res.status})`)
      }
      const data: GenerateResponse = await res.json()
      setGenerationResults(data.results)
      setSelectedIndices(new Set(data.results.filter((r) => !r.error).map((r) => r.variation_index)))
      if (data.total_failed > 0) {
        toast({ title: `${data.total_generated} generated, ${data.total_failed} failed`, variant: 'destructive' })
      } else {
        toast({ title: `${data.total_generated} variation${data.total_generated !== 1 ? 's' : ''} generated` })
      }
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    } catch (err) {
      toast({ title: 'Generation failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setGenerating(false)
    }
  }

  const handleSave = async (folderId: number | null, newFolderName: string) => {
    if (!generationResults) return
    setSaving(true)
    const selected = generationResults.filter((r) => selectedIndices.has(r.variation_index))
    try {
      let resolvedFolderId = folderId ?? 0
      if (newFolderName.trim() && !folderId) {
        const res = await apiJson<{ id: number }>('/api/v1/folders', {
          method: 'POST',
          body: JSON.stringify({ name: newFolderName.trim(), folder_type: 'general' }),
        })
        resolvedFolderId = res.id
      }
      await apiJson('/api/v1/avatars/save', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Product variation',
          description: analysisResult?.description ?? '',
          features: analysisResult?.features ?? [],
          folder_id: resolvedFolderId,
          image_type: 'product',
          selected: selected.map((r) => ({ local_path: r.local_path, variation_index: r.variation_index })),
        }),
      })
      setSaved(true)
      toast({ title: `${selected.length} image${selected.length !== 1 ? 's' : ''} saved to library` })
    } catch (err) {
      toast({ title: 'Save failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (uploadedPreview && uploadedFile) URL.revokeObjectURL(uploadedPreview)
    setSelectedAssetId(null)
    setUploadedFile(null)
    setUploadedPreview(null)
    setAnalyzing(false)
    setAnalysisResult(null)
    setVariationText('')
    setNumVariations(2)
    setModel(imageModels[0].id)
    setAspectRatio('1:1')
    setImageSize(imageModels[0].defaultSize)
    setGenerating(false)
    setGenerationResults(null)
    setSelectedIndices(new Set())
    setSaving(false)
    setSaved(false)
  }

  React.useEffect(() => {
    return () => { if (uploadedPreview && uploadedFile) URL.revokeObjectURL(uploadedPreview) }
  }, [uploadedPreview, uploadedFile])

  return (
    <div className="space-y-0">

      {/* ── STEP 1: Select product image ───────────────────────────────────── */}
      <div className="space-y-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="flex items-center text-sm font-semibold">
            <StepNumber n={1} />
            Select product image
          </p>
          <CompactUpload onFile={(f) => { void handleUpload(f) }} label="Upload from device" />
        </div>
        {uploadedPreview && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20">
            <img src={uploadedPreview} alt="Uploaded" className="h-14 w-14 rounded-md object-cover border border-border shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{uploadedFile?.name}</p>
              <p className="text-xs text-muted-foreground">Ready to generate from this image</p>
            </div>
            <button type="button" onClick={() => { if (uploadedPreview && uploadedFile) URL.revokeObjectURL(uploadedPreview); setUploadedFile(null); setUploadedPreview(null); setAnalysisResult(null) }}
              className="text-xs text-muted-foreground hover:text-foreground underline shrink-0">Remove</button>
          </div>
        )}
        <AssetGridPicker
          label="Or pick from library"
          assets={allAssets}
          folders={allFolders}
          selectedId={selectedAssetId}
          onSelect={handleAssetSelect}
          filterFolderIds={productFolderIds.length > 0 ? productFolderIds : undefined}
        />
      </div>

      <Separator className="my-0" />

      {/* ── STEP 2: AI Analysis ───────────────────────────────────────────── */}
      <div className="space-y-4 py-6" ref={step2Ref}>
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={2} />
          AI analysis
          {analyzing && <Loader2 className="h-4 w-4 animate-spin ml-2 text-muted-foreground" />}
          {hasAnalysis && !analyzing && <Check className="h-4 w-4 ml-2" />}
        </p>

        {!activeImageSource && !analyzing && (
          <p className="text-sm text-muted-foreground">Select or upload a product image above to auto-analyze.</p>
        )}

        {analyzing && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <div className="flex gap-1.5">{[70, 90, 60].map((w) => <Skeleton key={w} className="h-6 rounded-full" style={{ width: w }} />)}</div>
          </div>
        )}

        {hasAnalysis && !analyzing && analysisResult && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Description</Label>
              <p className="text-sm text-muted-foreground leading-relaxed rounded-md border border-border bg-muted/30 p-3">{analysisResult.description}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Detected features</Label>
              <div className="flex flex-wrap gap-1.5">
                {analysisResult.features.map((feat, i) => <Badge key={i} variant="secondary" className="text-xs">{feat}</Badge>)}
              </div>
            </div>
          </div>
        )}
      </div>

      <Separator className="my-0" />

      {/* ── STEP 3: Variation prompt ──────────────────────────────────────── */}
      <div className="space-y-3 py-6" ref={step3Ref}>
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={3} />
          Describe variation
        </p>
        <Textarea
          value={variationText}
          onChange={(e) => setVariationText(e.target.value)}
          placeholder="e.g. Place on marble countertop at golden hour, flat lay with ingredients, hand holding bottle close-up"
          rows={3}
          className="resize-none text-sm"
          disabled={!activeImageSource || analyzing}
          aria-label="Variation description"
        />
        <p className="text-xs text-muted-foreground">Combined with the product analysis to form the generation prompt.</p>
      </div>

      <Separator className="my-0" />

      {/* ── STEP 4: Settings + Generate ───────────────────────────────────── */}
      <div className="space-y-5 py-6" ref={step4Ref}>
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={4} />
          Settings &amp; generate
        </p>

        <div>
          <SectionLabel>Model</SectionLabel>
          <div className="flex gap-3">
            {imageModels.map((m) => (
              <button key={m.id} type="button" onClick={() => handleSwitchModel(m.id)} disabled={generating}
                className={['flex-1 rounded-lg border px-4 py-3 text-left transition-colors', model === m.id ? 'border-foreground bg-foreground/10' : 'border-border hover:bg-muted', generating ? 'opacity-50 cursor-not-allowed' : ''].join(' ')}>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{m.label} &middot; {m.price}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <SectionLabel>Aspect Ratio</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {selectedModel.aspectRatios.map((r) => <Pill key={r} label={r} selected={aspectRatio === r} onClick={() => setAspectRatio(r)} disabled={generating} />)}
            </div>
          </div>
          <div>
            <SectionLabel>Quality</SectionLabel>
            <div className="flex gap-2">
              {selectedModel.sizes.map((s) => <Pill key={s} label={s} selected={imageSize === s} onClick={() => setImageSize(s)} disabled={generating} flex />)}
            </div>
          </div>
          <div>
            <SectionLabel>Variations</SectionLabel>
            <div className="flex gap-2">
              {VARIATION_OPTIONS.map((n) => <Pill key={n} label={String(n)} selected={numVariations === n} onClick={() => setNumVariations(n)} disabled={generating} flex />)}
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">Estimated cost: {selectedModel.price} &times; {numVariations} = ~${estimatedCost}</p>

        <div className="flex gap-3">
          <button onClick={() => { void handleGenerate() }} disabled={!canGenerate} aria-busy={generating}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg border bg-foreground text-background px-6 py-3 font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors">
            {generating ? (
              <><Loader2 className="h-5 w-5 animate-spin" />Generating {numVariations} variation{numVariations > 1 ? 's' : ''}…</>
            ) : (
              <><Sparkles className="h-5 w-5" />Generate {numVariations} Variation{numVariations > 1 ? 's' : ''}</>
            )}
          </button>
          <button onClick={handleReset} disabled={generating} className="px-4 py-3 rounded-lg border hover:bg-muted transition-colors disabled:opacity-50 text-sm font-medium">Reset</button>
        </div>
      </div>

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {(generating || hasResults) && (
        <>
          <Separator className="my-0" />
          <div className="space-y-5 py-6" ref={resultsRef}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="flex items-center text-sm font-semibold"><StepNumber n={5} />Select to save</p>
              {hasResults && !generating && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">{selectedCount} of {successfulResults.length} selected</span>
                  <button onClick={() => setSelectedIndices(new Set(successfulResults.map((r) => r.variation_index)))} disabled={selectedCount === successfulResults.length} className="hover:underline disabled:opacity-40">All</button>
                  <button onClick={() => setSelectedIndices(new Set())} disabled={selectedCount === 0} className="text-muted-foreground hover:underline disabled:opacity-40">None</button>
                </div>
              )}
            </div>
            {generating && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" aria-live="polite">
                {Array.from({ length: numVariations }).map((_, i) => <div key={i} className="rounded-lg overflow-hidden border"><Skeleton className="aspect-square w-full" /></div>)}
              </div>
            )}
            {hasResults && !generating && generationResults && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" aria-live="polite">
                {generationResults.map((r) => (
                  <ResultCard key={r.variation_index} result={r} label="Variation" selected={selectedIndices.has(r.variation_index)}
                    onToggle={() => setSelectedIndices((prev) => { const next = new Set(prev); if (next.has(r.variation_index)) next.delete(r.variation_index); else next.add(r.variation_index); return next })}
                    saved={saved} />
                ))}
              </div>
            )}
          </div>

          <Separator className="my-0" />
          <div className="py-6">
            <p className="flex items-center text-sm font-semibold mb-4"><StepNumber n={6} />Save to library</p>
            <SaveSection folders={allFolders} successfulResults={successfulResults}
              saving={saving} saved={saved} selectedCount={selectedCount} savedCount={selectedCount}
              onSave={handleSave} onReset={handleReset} />
          </div>
        </>
      )}
    </div>
  )
}

// ─── Composite tab ──────────────────────────────────────────────────────────────

function CompositeTab({
  allAssets,
  allFolders,
}: {
  allAssets: Asset[]
  allFolders: AssetFolder[]
}) {
  const { toast } = useToast()

  // Selections
  const [selectedAvatarId, setSelectedAvatarId] = React.useState<number | null>(null)

  // Settings
  const [instructions, setInstructions] = React.useState<string>('')
  const [numVariations, setNumVariations] = React.useState<number>(2)
  const [aspectRatio, setAspectRatio] = React.useState<string>('1:1')
  const [quality, setQuality] = React.useState<string>('1K')

  // Generation
  const [generating, setGenerating] = React.useState(false)
  const [results, setResults] = React.useState<GenerationResult[] | null>(null)
  const [selectedIndices, setSelectedIndices] = React.useState<Set<number>>(new Set())
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [savedCount, setSavedCount] = React.useState(0)

  // Refs
  const step2Ref = React.useRef<HTMLDivElement>(null)
  const step3Ref = React.useRef<HTMLDivElement>(null)
  const resultsRef = React.useRef<HTMLDivElement>(null)

  // Product image — either KB product (multi-select up to 3) or direct upload
  const [productImages, setProductImages] = React.useState<Array<{ id?: number; file_path: string; role: string }>>([])
  // IDs of KB product images the user has toggled on (up to 3)
  const [selectedProductImageIds, setSelectedProductImageIds] = React.useState<number[]>([])
  const [uploadedProductAssetId, setUploadedProductAssetId] = React.useState<number | null>(null)
  const [uploadedProductPreview, setUploadedProductPreview] = React.useState<string | null>(null)
  const [uploadingProduct, setUploadingProduct] = React.useState(false)

  const MAX_PRODUCT_IMAGES = 3

  const toggleProductImage = (id: number) => {
    setSelectedProductImageIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= MAX_PRODUCT_IMAGES) return prev
      return [...prev, id]
    })
  }

  // Avatar — either library pick or direct upload
  const [uploadedAvatarAssetId, setUploadedAvatarAssetId] = React.useState<number | null>(null)
  const [uploadedAvatarPreview, setUploadedAvatarPreview] = React.useState<string | null>(null)
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false)

  // Fetch products for the dropdown
  const [products, setProducts] = React.useState<Array<{ id: number; name: string; price: string | null }>>([])
  const [selectedProductId, setSelectedProductId] = React.useState<number | null>(null)

  React.useEffect(() => {
    apiFetch('/api/v1/products').then(async (r) => {
      if (r.ok) {
        const d = await r.json()
        setProducts((d.items ?? d) as Array<{ id: number; name: string; price: string | null }>)
      }
    }).catch(() => {})
  }, [])

  // Fetch product detail images when selection changes
  React.useEffect(() => {
    if (!selectedProductId) { setProductImages([]); setSelectedProductImageIds([]); return }
    apiFetch(`/api/v1/products/${selectedProductId}`).then(async (r) => {
      if (!r.ok) return
      const d = await r.json() as { assets?: Array<{ role: string; asset: { id: number; file_path: string } }> }
      const imgs = (d.assets ?? []).map((a) => ({ id: a.asset.id, file_path: a.asset.file_path, role: a.role }))
      setProductImages(imgs)
      // Auto-select the first image (hero) when product changes
      setSelectedProductImageIds(imgs.length > 0 && imgs[0].id !== undefined ? [imgs[0].id] : [])
    }).catch(() => {})
  }, [selectedProductId])

  // Quick-save an uploaded file as an Asset via /assets/quick-save
  const quickSave = async (file: File, sceneType: string): Promise<{ id: number; file_path: string } | null> => {
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await apiFetch(`/api/v1/assets/quick-save?scene_type=${sceneType}`, { method: 'POST', body: fd })
      if (!res.ok) return null
      return await res.json() as { id: number; file_path: string }
    } catch {
      return null
    }
  }

  const handleProductUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please upload an image.', variant: 'destructive' })
      return
    }
    setUploadingProduct(true)
    const preview = URL.createObjectURL(file)
    setUploadedProductPreview(preview)
    const asset = await quickSave(file, '')
    if (asset) {
      setUploadedProductAssetId(asset.id)
      // Clear KB product selection when uploading directly
      setSelectedProductId(null)
      setProductImages([])
      setSelectedProductName(file.name.replace(/\.[^.]+$/, ''))
    } else {
      toast({ title: 'Upload failed', description: 'Could not save image.', variant: 'destructive' })
      URL.revokeObjectURL(preview)
      setUploadedProductPreview(null)
    }
    setUploadingProduct(false)
  }

  const handleAvatarUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please upload an image.', variant: 'destructive' })
      return
    }
    setUploadingAvatar(true)
    const preview = URL.createObjectURL(file)
    setUploadedAvatarPreview(preview)
    const asset = await quickSave(file, 'model')
    if (asset) {
      setUploadedAvatarAssetId(asset.id)
      setSelectedAvatarId(null) // clear library pick
      setSelectedAvatarLabel(file.name.replace(/\.[^.]+$/, ''))
      setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    } else {
      toast({ title: 'Upload failed', description: 'Could not save image.', variant: 'destructive' })
      URL.revokeObjectURL(preview)
      setUploadedAvatarPreview(null)
    }
    setUploadingAvatar(false)
  }

  const effectiveProductId = selectedProductId ?? 0 // 0 = no KB product (direct image used)
  const effectiveAvatarId = uploadedAvatarAssetId ?? selectedAvatarId
  const canGenerate = (selectedProductId !== null || uploadedProductAssetId !== null) && effectiveAvatarId !== null && !generating

  const handleAvatarSelect = (asset: Asset) => {
    setSelectedAvatarId(asset.id)
    setSelectedAvatarLabel(asset.original_filename
      ? asset.original_filename.replace(/\.[^.]+$/, '')
      : asset.filename.replace(/\.[^.]+$/, ''))
    setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
  }

  const successfulResults = React.useMemo(
    () => (results ?? []).filter((r) => !r.error),
    [results]
  )
  const selectedCount = selectedIndices.size

  // Store selected product name for tagging composites on save
  const [selectedProductName, setSelectedProductName] = React.useState<string>('')
  const [selectedAvatarLabel, setSelectedAvatarLabel] = React.useState<string>('')

  const handleGenerate = async () => {
    if (!canGenerate || effectiveAvatarId === null) return
    setGenerating(true)
    setResults(null)
    setSelectedIndices(new Set())
    setSaved(false)
    setSavedCount(0)
    try {
      // Build product image fields — support both single (legacy) and multi-select.
      const multiIds = uploadedProductAssetId !== null
        ? [uploadedProductAssetId]
        : selectedProductImageIds
      const res = await apiFetch(
        `/api/v1/products/${effectiveProductId}/composite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            avatar_asset_id: effectiveAvatarId,
            // Legacy single field (back-compat)
            ...(multiIds.length > 0 ? { product_image_asset_id: multiIds[0] } : {}),
            // New plural field for multi-angle support
            ...(multiIds.length > 0 ? { product_image_asset_ids: multiIds } : {}),
            num_variations: numVariations,
            aspect_ratio: aspectRatio,
            image_size: quality,
            ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
          }),
        }
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail ?? `Request failed (${res.status})`)
      }
      const data: CompositeResponse = await res.json()
      setResults(data.results)
      setSelectedIndices(new Set(data.results.filter((r) => !r.error).map((r) => r.variation_index)))
      if (data.total_failed > 0) {
        toast({ title: `${data.total_generated} generated, ${data.total_failed} failed`, variant: 'destructive' })
      } else {
        toast({ title: `${data.total_generated} composite${data.total_generated !== 1 ? 's' : ''} ready — choose where to save` })
      }
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    } catch (err) {
      toast({ title: 'Generation failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setGenerating(false)
    }
  }

  const handleSave = async (folderId: number | null, newFolderName: string) => {
    if (!results) return
    setSaving(true)
    const selected = results.filter((r) => selectedIndices.has(r.variation_index))
    try {
      let resolvedFolderId = folderId ?? 0
      if (newFolderName.trim() && !folderId) {
        const res = await apiJson<{ id: number }>('/api/v1/folders', {
          method: 'POST',
          body: JSON.stringify({ name: newFolderName.trim(), folder_type: 'general' }),
        })
        resolvedFolderId = res.id
      }
      await apiJson('/api/v1/products/save-composites', {
        method: 'POST',
        body: JSON.stringify({
          folder_id: resolvedFolderId,
          avatar_label: selectedAvatarLabel,
          product_name: selectedProductName,
          selected: selected.map((r) => ({ local_path: r.local_path, variation_index: r.variation_index })),
        }),
      })
      setSaved(true)
      setSavedCount(selected.length)
      toast({ title: `${selected.length} composite${selected.length !== 1 ? 's' : ''} saved to library` })
    } catch (err) {
      toast({ title: 'Save failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setSelectedProductId(null)
    setSelectedAvatarId(null)
    setProductImages([])
    setSelectedProductImageIds([])
    setSelectedProductName('')
    setSelectedAvatarLabel('')
    if (uploadedProductPreview) URL.revokeObjectURL(uploadedProductPreview)
    setUploadedProductAssetId(null)
    setUploadedProductPreview(null)
    if (uploadedAvatarPreview) URL.revokeObjectURL(uploadedAvatarPreview)
    setUploadedAvatarAssetId(null)
    setUploadedAvatarPreview(null)
    setInstructions('')
    setNumVariations(2)
    setAspectRatio('1:1')
    setQuality('1K')
    setResults(null)
    setSelectedIndices(new Set())
    setSaved(false)
    setSavedCount(0)
  }

  return (
    <div className="space-y-0">

      {/* ── STEP 1: Product image ──────────────────────────────────────────── */}
      <div className="space-y-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="flex items-center text-sm font-semibold">
            <StepNumber n={1} />
            Product image
          </p>
          <CompactUpload
            onFile={(f) => { void handleProductUpload(f) }}
            label={uploadingProduct ? 'Uploading…' : 'Upload from device'}
          />
        </div>

        {/* Uploaded product image preview */}
        {uploadedProductPreview && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20">
            <img src={uploadedProductPreview} alt="Product" className="h-16 w-16 object-cover rounded-md border border-border shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium">Uploaded product image</p>
              <p className="text-xs text-muted-foreground">Will be used as reference for the composite</p>
            </div>
            <button type="button" onClick={() => {
              if (uploadedProductPreview) URL.revokeObjectURL(uploadedProductPreview)
              setUploadedProductAssetId(null)
              setUploadedProductPreview(null)
            }} className="text-xs text-muted-foreground hover:text-foreground underline shrink-0">Remove</button>
          </div>
        )}

        {/* KB product dropdown */}
        {!uploadedProductPreview && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Or choose from your Products KB:</p>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground">No products yet — upload an image above or add products in the Products page.</p>
            ) : (
              <>
                <select
                  value={selectedProductId ?? ''}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null
                    setSelectedProductId(id)
                    const pName = products.find((p) => p.id === id)?.name ?? ''
                    setSelectedProductName(pName)
                    if (id) setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
                  }}
                  className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Choose a product…</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.price ? ` — ${p.price}` : ''}
                    </option>
                  ))}
                </select>
                {selectedProductId && productImages.length === 0 && (
                  <p className="text-xs text-muted-foreground">No hero image linked to this product yet.</p>
                )}
                {productImages.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[11px] text-muted-foreground">
                      Pick 1-3 product angles (front, 3/4, label close-up) for best label accuracy.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {productImages.map((img, i) => {
                        const imgId = img.id
                        const isSelected = imgId !== undefined && selectedProductImageIds.includes(imgId)
                        const selIdx = imgId !== undefined ? selectedProductImageIds.indexOf(imgId) : -1
                        const atMax = selectedProductImageIds.length >= MAX_PRODUCT_IMAGES
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => { if (imgId !== undefined) toggleProductImage(imgId) }}
                            disabled={generating || (atMax && !isSelected) || imgId === undefined}
                            aria-pressed={isSelected}
                            className={[
                              'relative h-20 w-20 rounded-md overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              isSelected ? 'border-foreground' : 'border-border hover:border-foreground/50',
                              (atMax && !isSelected) || imgId === undefined ? 'opacity-40 cursor-not-allowed' : '',
                            ].filter(Boolean).join(' ')}
                          >
                            <img
                              src={`/uploads/${img.file_path}`}
                              alt={`Product angle ${i + 1}`}
                              className="h-full w-full object-cover"
                            />
                            {img.role === 'hero_image' && !isSelected && (
                              <span className="absolute bottom-1 left-1 text-[9px] bg-foreground/80 text-background rounded px-1 leading-4">hero</span>
                            )}
                            {isSelected && (
                              <span className="absolute top-1 left-1 h-5 w-5 rounded-full bg-foreground text-background text-[10px] font-bold flex items-center justify-center">
                                {selIdx + 1}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                    {selectedProductImageIds.length >= MAX_PRODUCT_IMAGES && (
                      <p className="text-[11px] text-muted-foreground">
                        Max {MAX_PRODUCT_IMAGES} images selected. Deselect one to swap.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <Separator className="my-0" />

      {/* ── STEP 2: Avatar ────────────────────────────────────────────────── */}
      <div className="space-y-4 py-6" ref={step2Ref}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="flex items-center text-sm font-semibold">
            <StepNumber n={2} />
            Avatar / model
          </p>
          <CompactUpload
            onFile={(f) => { void handleAvatarUpload(f) }}
            label={uploadingAvatar ? 'Uploading…' : 'Upload from device'}
          />
        </div>

        {/* Uploaded avatar preview */}
        {uploadedAvatarPreview && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20">
            <img src={uploadedAvatarPreview} alt="Avatar" className="h-16 w-16 object-cover rounded-md border border-border shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium">Uploaded avatar image</p>
              <p className="text-xs text-muted-foreground">Will be used as the model in the composite</p>
            </div>
            <button type="button" onClick={() => {
              if (uploadedAvatarPreview) URL.revokeObjectURL(uploadedAvatarPreview)
              setUploadedAvatarAssetId(null)
              setUploadedAvatarPreview(null)
            }} className="text-xs text-muted-foreground hover:text-foreground underline shrink-0">Remove</button>
          </div>
        )}

        {/* Library picker */}
        {!uploadedAvatarPreview && (
          <>
            <p className="text-xs text-muted-foreground">Or pick from your avatar library:</p>
            <AssetGridPicker
              label="Your avatars"
              assets={allAssets}
              folders={allFolders}
              selectedId={selectedAvatarId}
              onSelect={handleAvatarSelect}
              filterSceneType="model"
            />
          </>
        )}
      </div>

      <Separator className="my-0" />

      {/* ── STEP 3: Settings + Generate ───────────────────────────────────── */}
      <div className="space-y-5 py-6" ref={step3Ref}>
        <p className="flex items-center text-sm font-semibold">
          <StepNumber n={3} />
          Settings &amp; generate
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <SectionLabel>Variations</SectionLabel>
            <div className="flex gap-1.5">
              {VARIATION_OPTIONS.map((n) => <Pill key={n} label={String(n)} selected={numVariations === n} onClick={() => setNumVariations(n)} disabled={generating} flex />)}
            </div>
          </div>
          <div>
            <SectionLabel>Aspect Ratio</SectionLabel>
            <div className="flex gap-1.5">
              {COMPOSITE_ASPECT_RATIOS.map((ar) => <Pill key={ar} label={ar} selected={aspectRatio === ar} onClick={() => setAspectRatio(ar)} disabled={generating} />)}
            </div>
          </div>
          <div>
            <SectionLabel>Quality</SectionLabel>
            <div className="flex gap-1.5">
              {COMPOSITE_QUALITY_OPTIONS.map((q) => <Pill key={q} label={q} selected={quality === q} onClick={() => setQuality(q)} disabled={generating} flex />)}
            </div>
          </div>
        </div>

        {/* Scene instructions */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Scene instructions <span className="normal-case font-normal">(optional)</span>
            </label>
            {instructions && (
              <button type="button" onClick={() => setInstructions('')} className="text-xs text-muted-foreground hover:text-foreground">
                Clear
              </button>
            )}
          </div>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            disabled={generating}
            placeholder={'e.g. "Doctor sitting at a desk, holding the product up toward camera" or "Avatar applying the oil at a vanity mirror"'}
            rows={2}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none placeholder:text-muted-foreground/50 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">Leave blank for default: avatar holding product, studio lighting.</p>
        </div>

        {!canGenerate && !generating && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert">
            {(selectedProductId === null && uploadedProductAssetId === null) ? 'Select or upload a product image above to continue.' : 'Select or upload an avatar above to continue.'}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={() => { void handleGenerate() }} disabled={!canGenerate} aria-busy={generating}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg border bg-foreground text-background px-6 py-3 font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors">
            {generating ? (
              <><Loader2 className="h-5 w-5 animate-spin" />Generating {numVariations} composite{numVariations > 1 ? 's' : ''}…</>
            ) : (
              <><Layers className="h-5 w-5" />Generate {numVariations} Composite{numVariations > 1 ? 's' : ''}</>
            )}
          </button>
          <button onClick={handleReset} disabled={generating} className="px-4 py-3 rounded-lg border hover:bg-muted transition-colors disabled:opacity-50 text-sm font-medium">Reset</button>
        </div>
      </div>

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {(generating || results !== null) && (
        <>
          <Separator className="my-0" />
          <div className="space-y-5 py-6" ref={resultsRef}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="flex items-center text-sm font-semibold"><StepNumber n={4} />Select composites</p>
              {results && !generating && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">{selectedCount} of {successfulResults.length} selected</span>
                  <button onClick={() => setSelectedIndices(new Set(successfulResults.map((r) => r.variation_index)))} disabled={selectedCount === successfulResults.length} className="hover:underline disabled:opacity-40">All</button>
                  <button onClick={() => setSelectedIndices(new Set())} disabled={selectedCount === 0} className="text-muted-foreground hover:underline disabled:opacity-40">None</button>
                </div>
              )}
            </div>
            {generating && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" aria-live="polite">
                {Array.from({ length: numVariations }).map((_, i) => <div key={i} className="rounded-lg overflow-hidden border"><Skeleton className="aspect-square w-full" /></div>)}
              </div>
            )}
            {results && !generating && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" aria-live="polite">
                {results.map((r) => (
                  <ResultCard key={r.variation_index} result={r} label="Composite"
                    selected={selectedIndices.has(r.variation_index)}
                    onToggle={() => setSelectedIndices((prev) => {
                      const next = new Set(prev)
                      if (next.has(r.variation_index)) next.delete(r.variation_index)
                      else next.add(r.variation_index)
                      return next
                    })}
                    saved={saved} />
                ))}
              </div>
            )}
          </div>

          <Separator className="my-0" />
          <div className="py-6">
            <p className="flex items-center text-sm font-semibold mb-4"><StepNumber n={5} />Save to library</p>
            <SaveSection
              folders={allFolders}
              successfulResults={successfulResults}
              saving={saving}
              saved={saved}
              selectedCount={selectedCount}
              savedCount={savedCount}
              onSave={handleSave}
              onReset={handleReset}
            />
          </div>
        </>
      )}
    </div>
  )
}

// ─── Storyboard tab ───────────────────────────────────────────────────────────
//
// Generates a multi-panel storyboard image to use as a Seedance multimodal
// reference. The whole point: stylized panels avoid the photo-real face
// moderation that gates Seedance's image-to-video path on OpenRouter, while
// still locking character + sequence so the resulting video has continuity.
// Pattern documented in the Runway help-center article and used in the
// Wildflower Matcha production prompt the client showed us.

// Panel options depend on the usePanelPairs toggle:
//   pairs OFF (default): 4, 6, 9 — one panel per scene, max 9 panels
//   pairs ON:            2, 4, 6, 8, 10 — two panels per scene (open + close frame)
const STORYBOARD_PANEL_OPTIONS_DEFAULT = [4, 6, 9] as const
const STORYBOARD_PANEL_OPTIONS_PAIRS = [2, 4, 6, 8, 10] as const
const STORYBOARD_PANEL_OPTIONS = STORYBOARD_PANEL_OPTIONS_DEFAULT
const STORYBOARD_GRID: Record<number, string> = {
  2: '2×1', 4: '2×2', 6: '3×2', 8: '4×2', 9: '3×3', 10: '5×2',
}

type StoryboardPanelCount = number
type StoryboardAspect = '16:9' | '1:1' | '9:16' | '4:3'
type StoryboardProvider = 'gemini' | 'openrouter'
// Render style:
//   illustration = line + watercolor wash designer storyboard with stylized
//                  people. Safe Seedance ref because illustrated faces don't
//                  trigger face moderation. Carries composition + blocking
//                  + body language. DEFAULT for human-centered ads.
//   standalone   = photoreal cinematic, NO VISIBLE FACES (bodies / hands /
//                  silhouettes OK; Seedance-safe ref). Use for product-only.
//   with_face    = photoreal cinematic WITH a real-looking person INCLUDING
//                  their face (client sign-off deliverable; Seedance face
//                  moderation may reject as ref).
type StoryboardStyle = 'illustration' | 'standalone' | 'with_face'

interface StoryboardPanel {
  number: number
  scene_name: string
  action_vo: string
  vo_quote: string
  image_description: string
}

interface StoryboardPlan {
  title: string
  subtitle: string
  brand: string
  product: string
  subject_anchor: string
  product_anchor: string
  palette: string
  lighting: string
  lens: string
  panels: StoryboardPanel[]
  end_card: {
    tagline: string
    cta: string
    logo_text: string
    product_subtitle: string
  }
  meta?: {
    cost_cents?: number
    provider?: string
    model?: string
    generation_time_seconds?: number
  }
}

interface StoryboardGenerateResponse {
  job_id: number
  plan: StoryboardPlan
}

interface ProductOption {
  id: number
  name: string
}

interface PlanModelOption {
  id: StoryboardProvider
  label: string
  model: string
  available: boolean
  pricing?: { input_per_1m_usd: number; output_per_1m_usd: number }
}

interface StoryboardRefDraft {
  asset_id: number
  file_path: string
  filename: string
  description: string
}

const MAX_STORYBOARD_REFS = 4

function StoryboardTab({ allAssets, allFolders }: { allAssets: Asset[]; allFolders: AssetFolder[] }) {
  const navigate = useNavigate()
  const { toast } = useToast()

  // User input
  const [brief, setBrief] = React.useState('')
  const [usePanelPairs, setUsePanelPairs] = React.useState(false)
  const [panels, setPanels] = React.useState<StoryboardPanelCount>(6)
  const [productId, setProductId] = React.useState<number | null>(null)
  const [aspectRatio, setAspectRatio] = React.useState<StoryboardAspect>('16:9')
  const [planProvider, setPlanProvider] = React.useState<StoryboardProvider>('gemini')
  const [style, setStyle] = React.useState<StoryboardStyle>('illustration')
  const [references, setReferences] = React.useState<StoryboardRefDraft[]>([])
  const [refUploading, setRefUploading] = React.useState(false)
  const [showLibraryPicker, setShowLibraryPicker] = React.useState(false)
  const refInputRef = React.useRef<HTMLInputElement>(null)

  // Async state
  type Stage = 'idle' | 'planning' | 'rendering' | 'done' | 'error'
  const [stage, setStage] = React.useState<Stage>('idle')
  const [plan, setPlan] = React.useState<StoryboardPlan | null>(null)
  const [pollingJobId, setPollingJobId] = React.useState<number | null>(null)
  const [resultUrl, setResultUrl] = React.useState<string | null>(null)
  const [resultJobId, setResultJobId] = React.useState<number | null>(null)
  const [resultAssetId, setResultAssetId] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [savingFolderId, setSavingFolderId] = React.useState<string>('')

  // Options
  const [products, setProducts] = React.useState<ProductOption[]>([])
  const [planModels, setPlanModels] = React.useState<PlanModelOption[]>([])

  // Load products + plan-model catalog on mount
  React.useEffect(() => {
    apiJson<{ items: Array<{ id: number; name: string }> }>('/api/v1/products?limit=200')
      .then((data) => {
        setProducts((data.items ?? []).map((p) => ({ id: p.id, name: p.name })))
      })
      .catch(() => setProducts([]))

    apiJson<{ items: PlanModelOption[]; default: StoryboardProvider }>('/api/v1/scripts/models')
      .then((data) => {
        setPlanModels(data.items)
        if (data.default === 'gemini' || data.default === 'openrouter') {
          setPlanProvider(data.default)
        }
      })
      .catch(() => setPlanModels([]))
  }, [])

  const isBusy = stage === 'planning' || stage === 'rendering'
  const canGenerate = brief.trim().length >= 10 && !isBusy

  // Upload one or more reference images and add them to the references
  // list. Multi-select; each becomes a draft with empty description the
  // user fills in below the thumbnail.
  const handleRefUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList)
    const remaining = MAX_STORYBOARD_REFS - references.length
    if (remaining <= 0) {
      toast({
        title: `Maximum ${MAX_STORYBOARD_REFS} references`,
        description: 'Remove one to upload another.',
        variant: 'destructive',
      })
      e.target.value = ''
      return
    }
    setRefUploading(true)
    try {
      const accepted = files.slice(0, remaining)
      const results = await Promise.allSettled(
        accepted.map(async (file) => {
          const fd = new FormData()
          fd.append('file', file)
          const res = await apiFetch('/api/v1/assets/quick-save', { method: 'POST', body: fd })
          if (!res.ok) throw new Error(`upload failed: ${file.name}`)
          return (await res.json()) as Asset
        }),
      )
      const newRefs: StoryboardRefDraft[] = []
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          newRefs.push({
            asset_id: r.value.id,
            file_path: r.value.file_path,
            filename: r.value.original_filename ?? r.value.filename,
            description: '',
          })
        }
      })
      setReferences((prev) => [...prev, ...newRefs])
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) {
        toast({ title: `${failed} upload${failed === 1 ? '' : 's'} failed`, variant: 'destructive' })
      }
    } finally {
      setRefUploading(false)
      if (refInputRef.current) refInputRef.current.value = ''
    }
  }

  const updateRefDescription = (assetId: number, desc: string) => {
    setReferences((prev) =>
      prev.map((r) => (r.asset_id === assetId ? { ...r, description: desc } : r)),
    )
  }

  const removeRef = (assetId: number) => {
    setReferences((prev) => prev.filter((r) => r.asset_id !== assetId))
  }

  const handleTogglePanelPairs = (next: boolean) => {
    setUsePanelPairs(next)
    const validOptions = next ? STORYBOARD_PANEL_OPTIONS_PAIRS : STORYBOARD_PANEL_OPTIONS_DEFAULT
    if (!validOptions.includes(panels as any)) {
      // Snap to closest valid value
      const closest = (validOptions as readonly number[]).reduce((prev, curr) =>
        Math.abs(curr - panels) < Math.abs(prev - panels) ? curr : prev,
      )
      setPanels(closest)
    }
  }

  const handleGenerate = async () => {
    setStage('planning')
    setError(null)
    setPlan(null)
    setResultUrl(null)
    setResultJobId(null)
    setResultAssetId(null)
    try {
      const response = await apiJson<StoryboardGenerateResponse>(
        '/api/v1/storyboards/generate',
        {
          method: 'POST',
          body: JSON.stringify({
            brief: brief.trim(),
            panel_count: panels,
            use_panel_pairs: usePanelPairs,
            product_id: productId,
            aspect_ratio: aspectRatio,
            model_provider: planProvider,
            style,
            references: references.map((r) => ({
              asset_id: r.asset_id,
              description: r.description.trim() || undefined,
            })),
          }),
        },
      )
      setPlan(response.plan)
      setStage('rendering')
      setPollingJobId(response.job_id)
    } catch (err) {
      setError((err as Error).message)
      setStage('error')
    }
  }

  // Poll the image-render job until completed/failed.
  React.useEffect(() => {
    if (!pollingJobId) return
    let cancelled = false
    const tick = async () => {
      try {
        const data = await apiJson<{
          id: number
          status: string
          output_url: string | null
          error_message: string | null
        }>(`/api/v1/jobs/${pollingJobId}`)
        if (cancelled) return
        if (data.status === 'completed' && data.output_url) {
          setResultUrl(data.output_url)
          setResultJobId(data.id)
          setPollingJobId(null)
          setStage('done')
        } else if (data.status === 'failed') {
          setError(data.error_message ?? 'Image render failed.')
          setPollingJobId(null)
          setStage('error')
        }
      } catch {
        /* keep polling */
      }
    }
    void tick()
    const handle = window.setInterval(tick, 4000)
    return () => {
      cancelled = true
      window.clearInterval(handle)
    }
  }, [pollingJobId])

  // Save the rendered storyboard to Saved Context. Returns the new asset id,
  // or null on failure. Marks the asset with scene_type="storyboard" so the
  // script generator can detect storyboard mode without waiting for the
  // ai_description analysis task to finish.
  const saveToLibrary = React.useCallback(
    async (silent = false): Promise<number | null> => {
      if (!resultJobId) return null
      try {
        const folderIdNum = savingFolderId ? Number(savingFolderId) : undefined
        const saved = await apiJson<{ asset_id: number }>(
          `/api/v1/jobs/${resultJobId}/save-to-library`,
          {
            method: 'POST',
            body: JSON.stringify({
              ...(folderIdNum ? { folder_id: folderIdNum } : {}),
              scene_type: 'storyboard',
            }),
          },
        )
        setResultAssetId(saved.asset_id)
        if (!silent) {
          toast({ title: 'Saved to context', description: 'Use it in Create Ad as a Seedance reference.' })
        }
        return saved.asset_id
      } catch (err) {
        toast({ title: 'Save failed', description: (err as Error).message, variant: 'destructive' })
        return null
      }
    },
    [resultJobId, savingFolderId, toast],
  )

  const handleSaveToLibrary = () => {
    void saveToLibrary(false)
  }

  // "Use in Create Ad" auto-saves first if not yet saved — guards against
  // the user clicking Use before Save and landing on Create Ad with no
  // attached storyboard.
  const handleUseInCreateAd = async () => {
    let assetId = resultAssetId
    if (!assetId) {
      assetId = await saveToLibrary(true)
      if (!assetId) return
    }
    navigate(`/create?storyboard_asset_id=${assetId}`)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Storyboard
            <span className="text-[10px] font-normal text-muted-foreground rounded px-1.5 py-0.5 border border-border">
              GPT-5.4 Image 2 · 2K
            </span>
          </h2>
          <p className="text-sm text-muted-foreground">
            Describe your ad in a sentence or two. We'll plan a panel-by-panel storyboard
            and render it as a production-grade reference image you can use in Create Ad.
          </p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="shrink-0">
              <BookOpen className="h-3.5 w-3.5" />
              How to use
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>How the storyboard workflow works</DialogTitle>
              <DialogDescription>
                Two stages: a creative-director LLM writes the plan, then GPT-5.4 Image 2 renders it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm leading-relaxed">
              <ol className="space-y-2 list-decimal pl-4">
                <li>
                  <span className="font-medium">Write a short brief.</span>{' '}
                  Two or three sentences describing the ad — subject, setting, tone. Optionally
                  pick a product from your KB; we'll fold its name, description, and brand voice
                  into the plan automatically.
                </li>
                <li>
                  <span className="font-medium">Pick board length and aspect ratio.</span>{' '}
                  4 panels for cheap iteration; 12 for a full client deliverable.
                </li>
                <li>
                  <span className="font-medium">Generate.</span>{' '}
                  Stage 1 (~3s): the LLM writes a structured plan with scene names, action/VO,
                  voice-over quotes, palette, and lens. Stage 2 (~30-60s): GPT-5.4 Image 2 renders
                  the storyboard at 2K with proper text labels per panel.
                </li>
                <li>
                  <span className="font-medium">Click "Use in Create Ad".</span>{' '}
                  We auto-save the storyboard image AND its structured plan. The script
                  generator reads the plan and writes per-scene Seedance prompts that match
                  each panel beat-for-beat.
                </li>
              </ol>
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
                <p className="font-medium text-foreground mb-1">Why two stages</p>
                <p className="text-muted-foreground">
                  GPT-5.4 Image 2 is best-in-class at text rendering and multi-panel layouts but
                  needs detailed instructions per panel. Letting an LLM write those instructions
                  from your brief produces dramatically more consistent results than a static
                  scaffold the user has to fill in. Cost: ~$0.005 for the plan + ~$0.225 for
                  the 2K image render.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Brief */}
      <div className="space-y-2">
        <Label htmlFor="storyboard-brief" className="text-sm font-semibold">
          Brief
        </Label>
        <Textarea
          id="storyboard-brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder='e.g. "30-second body oil ad. Woman in her late 50s, silver hair, sunlit bathroom, linen robe. Tone: calm, intentional, premium spa."'
          rows={4}
          className="resize-y text-sm"
          disabled={isBusy}
        />
        <p className="text-xs text-muted-foreground">
          Two to four sentences is plenty. The LLM fills in the rest.
        </p>
      </div>

      {/* Render style — three modes:
            illustration: line+wash storyboard with stylized people (DEFAULT,
                          best for Seedance ref — drawn faces don't moderate)
            standalone:   photoreal with no faces (for product-only ads)
            with_face:    photoreal with face (client sign-off, NOT for Seedance) */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold flex items-center gap-1.5">
          Render style
          <HelpHint
            text="Illustration (default) — line + watercolor wash designer storyboard with stylized people. Seedance accepts this as a multimodal reference because the drawn faces don't trigger face moderation, and the panels carry composition + blocking + body language for the downstream video to anchor against. Pick this for human-centered ads. Standalone — photoreal panels with NO VISIBLE FACES (bodies / hands / silhouettes OK). Use for product-only ads where composition is the product, not people. With Face — photoreal panels with real-looking faces. Client sign-off ONLY; Seedance face moderation will reject this as a multimodal reference."
            side="right"
          />
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {([
            {
              id: 'illustration' as const,
              title: 'Illustration',
              tagline: 'Line + wash · stylized people · Seedance-safe',
              detail:
                'Designer storyboard look. People appear with stylized faces (not photoreal) so Seedance accepts it as a ref and renders the photoreal face from your scene prompt at video time. Best default for human-centered ads.',
            },
            {
              id: 'standalone' as const,
              title: 'Standalone',
              tagline: 'Photoreal · no faces · Seedance-safe',
              detail:
                'Photoreal panels with bodies, hands, and silhouettes — but never a visible face. Best for product-only ads where you don\'t need people in the storyboard.',
            },
            {
              id: 'with_face' as const,
              title: 'With Face',
              tagline: 'Photoreal · face shown · client sign-off',
              detail:
                'Photoreal panels with a real-looking person, face shown. Great for client sign-off; Seedance face moderation may reject if you try to use it as a ref.',
            },
          ]).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setStyle(opt.id)}
              disabled={isBusy}
              className={[
                'rounded-md border px-3 py-2.5 text-left transition-colors',
                style === opt.id
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/50',
              ].join(' ')}
            >
              <div className="text-sm font-semibold">{opt.title}</div>
              <div className="text-[11px] mt-0.5 opacity-80">{opt.tagline}</div>
              <div className="text-[11px] mt-1 opacity-70 leading-snug">{opt.detail}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Board length + panel-pairs toggle */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Label className="text-sm font-semibold">Board length</Label>
          {/* Panel-pairs toggle */}
          <div className="flex items-start gap-2">
            <div className="flex flex-col gap-0.5 text-right">
              <span className="text-xs font-medium leading-none inline-flex items-center justify-end gap-1">
                Start + end frames per scene
                <HelpHint
                  text="When ON, each scene gets TWO panels — its opening frame and its closing frame. Gives the downstream Seedance video gen precise visual anchors for each beat (the start panel guides scene N's opening composition, the end panel guides the closing beat). Caps storyboard at 10 panels = 5 scenes. When OFF, one panel per scene, max 9 panels — simpler boards for quick concept review."
                  side="left"
                />
              </span>
              <span className="text-[10px] text-muted-foreground leading-snug">
                {usePanelPairs
                  ? 'ON — 2 panels/scene. Max 10 panels = 5 scenes.'
                  : 'OFF — 1 panel/scene. Up to 9 panels.'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleTogglePanelPairs(!usePanelPairs)}
              disabled={isBusy}
              className={[
                'shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2',
                usePanelPairs ? 'bg-foreground' : 'bg-border',
                isBusy ? 'opacity-50 cursor-not-allowed' : '',
              ].join(' ')}
              role="switch"
              aria-checked={usePanelPairs}
              aria-label="Start and end frames per scene"
            >
              <span
                className={[
                  'inline-block h-3.5 w-3.5 rounded-full bg-background transition-transform',
                  usePanelPairs ? 'translate-x-4' : 'translate-x-0.5',
                ].join(' ')}
              />
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(usePanelPairs ? STORYBOARD_PANEL_OPTIONS_PAIRS : STORYBOARD_PANEL_OPTIONS_DEFAULT).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setPanels(n)}
              disabled={isBusy}
              className={[
                'flex-1 min-w-[90px] rounded-md border px-4 py-2 text-sm transition-colors',
                panels === n
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/50',
              ].join(' ')}
            >
              <span className="font-semibold">{n} panels</span>
              <span className="block text-[10px] mt-0.5 opacity-70">{STORYBOARD_GRID[n] ?? ''} grid</span>
            </button>
          ))}
        </div>
      </div>

      {/* Product (optional) */}
      <div className="space-y-2">
        <Label htmlFor="storyboard-product" className="text-sm font-semibold">
          Product (optional)
        </Label>
        <select
          id="storyboard-product"
          value={productId ?? ''}
          onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : null)}
          disabled={isBusy}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">None — let the AI infer a product matching the brief</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          When set, we feed the product's full KB context (name, description, benefits, brand voice)
          into the plan. If the product has linked images, the hero photo is also passed to GPT
          Image 2 as a visual anchor.
        </p>
      </div>

      {/* Reference images (logo, lifestyle inspiration, etc.) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-semibold flex items-center gap-1.5">
            Reference images
            <span className="text-[10px] font-normal text-muted-foreground">optional</span>
            <HelpHint
              text="Upload up to 4 visual references (your logo, an inspiration shot, brand colors, an existing ad) OR pick from your asset library. Describe each one — the AI uses your descriptions to plan the storyboard around them, and the actual image is passed to GPT-5.4 Image 2 so it can match style, place a logo on the end card, etc."
              side="right"
            />
          </Label>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy || references.length >= MAX_STORYBOARD_REFS}
              onClick={() => setShowLibraryPicker((p) => !p)}
            >
              {showLibraryPicker ? 'Hide library' : 'Pick from library'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy || refUploading || references.length >= MAX_STORYBOARD_REFS}
              onClick={() => refInputRef.current?.click()}
            >
              {refUploading ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
              ) : (
                <><Plus className="h-3.5 w-3.5" /> Upload</>
              )}
            </Button>
          </div>
          <input
            ref={refInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={handleRefUpload}
            disabled={isBusy || refUploading}
          />
        </div>

        {showLibraryPicker && (
          <div className="rounded-md border border-border p-3 space-y-2">
            {(() => {
              const libraryImages = allAssets.filter((a) => a.file_type === 'image')
              if (libraryImages.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground py-2 text-center">
                    No images in your library yet. Upload via the button above or in{' '}
                    <a href="/saved-context" className="underline hover:text-foreground">
                      Saved Context
                    </a>
                    .
                  </p>
                )
              }
              const pickedIds = new Set(references.map((r) => r.asset_id))
              const atMax = references.length >= MAX_STORYBOARD_REFS
              return (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-72 overflow-y-auto">
                  {libraryImages.map((a) => {
                    const selected = pickedIds.has(a.id)
                    const disabled = !selected && atMax
                    return (
                      <button
                        key={a.id}
                        type="button"
                        disabled={disabled || isBusy}
                        aria-pressed={selected}
                        onClick={() => {
                          if (selected) {
                            setReferences((prev) => prev.filter((r) => r.asset_id !== a.id))
                            return
                          }
                          setReferences((prev) => [
                            ...prev,
                            {
                              asset_id: a.id,
                              file_path: a.file_path,
                              filename: a.original_filename ?? a.filename,
                              description: a.ai_description ?? '',
                            },
                          ])
                        }}
                        title={a.original_filename ?? a.filename}
                        className={
                          selected
                            ? 'relative aspect-square rounded overflow-hidden border-2 border-foreground ring-1 ring-foreground/50'
                            : disabled
                              ? 'relative aspect-square rounded overflow-hidden border border-border opacity-40 cursor-not-allowed'
                              : 'relative aspect-square rounded overflow-hidden border border-border hover:border-foreground/40 transition-colors'
                        }
                      >
                        <img
                          src={`/uploads/${a.file_path}`}
                          alt={a.original_filename ?? a.filename}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        {selected && (
                          <span className="absolute top-0.5 right-0.5 rounded-full bg-foreground text-background text-[10px] px-1 leading-tight">
                            ✓
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}

        {references.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            None attached. Add a logo, an inspiration shot, brand colors — anything that should
            guide the storyboard's visual direction.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {references.map((ref) => (
              <div key={ref.asset_id} className="rounded-md border border-border p-2 space-y-2">
                <div className="flex items-start gap-2">
                  <img
                    src={`/uploads/${ref.file_path}`}
                    alt={ref.filename}
                    className="w-16 h-16 rounded-md object-cover border border-border shrink-0"
                  />
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-xs font-medium truncate" title={ref.filename}>
                      {ref.filename}
                    </p>
                    <Input
                      type="text"
                      placeholder="Describe this image (e.g. 'brand logo', 'lifestyle inspiration')"
                      value={ref.description}
                      onChange={(e) => updateRefDescription(ref.asset_id, e.target.value)}
                      disabled={isBusy}
                      className="text-xs h-8"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRef(ref.asset_id)}
                    disabled={isBusy}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    aria-label="Remove reference"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aspect ratio */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Aspect ratio</Label>
        <div className="flex gap-2 flex-wrap">
          {(['16:9', '1:1', '9:16', '4:3'] as const).map((ar) => (
            <button
              key={ar}
              type="button"
              onClick={() => setAspectRatio(ar)}
              disabled={isBusy}
              className={[
                'rounded-md border px-3 py-1.5 text-xs transition-colors',
                aspectRatio === ar
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/50',
              ].join(' ')}
            >
              {ar}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          16:9 is standard for client-deliverable storyboards. Image renders at 2K longest side.
        </p>
      </div>

      {/* Plan model selector */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold flex items-center gap-1.5">
          Plan writer
          <HelpHint
            text="Which LLM writes the structured plan from your brief. Gemini Flash is fast and cheap (~$0.005 per plan). OpenRouter GPT-5 Mini sometimes produces stronger creative direction at slightly higher cost."
            side="right"
          />
        </Label>
        <div className="flex gap-2 flex-wrap">
          {(['gemini', 'openrouter'] as const).map((p) => {
            const opt = planModels.find((m) => m.id === p)
            const disabled = isBusy || (opt && !opt.available)
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPlanProvider(p)}
                disabled={disabled}
                className={[
                  'rounded-md border px-3 py-1.5 text-xs transition-colors',
                  planProvider === p
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/50',
                  disabled ? 'opacity-50 cursor-not-allowed' : '',
                ].join(' ')}
              >
                {opt?.label ?? (p === 'gemini' ? 'Gemini Flash' : 'OpenRouter GPT-5 Mini')}
              </button>
            )
          })}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <Button
        type="button"
        onClick={handleGenerate}
        disabled={!canGenerate}
        className="w-full sm:w-auto"
      >
        {isBusy ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {stage === 'planning' ? 'Writing the plan…' : 'Rendering panels at 2K…'}
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            Generate storyboard
          </>
        )}
      </Button>

      {/* Plan details — collapsible, shown as soon as Stage A returns */}
      {plan && (
        <details className="group rounded-lg border border-border bg-muted/20">
          <summary className="cursor-pointer text-sm font-medium px-4 py-3 flex items-center gap-2">
            <span>Plan details</span>
            <span className="text-xs text-muted-foreground">
              · {plan.title} · {plan.panels.length} panels
              {plan.meta?.cost_cents !== undefined && (
                <> · ${(plan.meta.cost_cents / 100).toFixed(4)}</>
              )}
            </span>
          </summary>
          <div className="px-4 pb-4 pt-1 space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-muted-foreground">Brand · Product</p>
                <p className="font-medium">{plan.brand} · {plan.product}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Subtitle</p>
                <p className="font-medium">{plan.subtitle}</p>
              </div>
            </div>
            {plan.subject_anchor && (
              <div>
                <p className="text-muted-foreground">Subject anchor</p>
                <p className="leading-relaxed">{plan.subject_anchor}</p>
              </div>
            )}
            {plan.product_anchor && (
              <div>
                <p className="text-muted-foreground">Product anchor</p>
                <p className="leading-relaxed">{plan.product_anchor}</p>
              </div>
            )}
            <div className="space-y-2 pt-2 border-t border-border">
              {plan.panels.map((p) => (
                <div key={p.number} className="space-y-0.5">
                  <p className="font-semibold">
                    {String(p.number).padStart(2, '0')} — {p.scene_name}
                  </p>
                  {p.action_vo && <p className="text-muted-foreground">Action: {p.action_vo}</p>}
                  {p.vo_quote && <p className="text-muted-foreground">VO: "{p.vo_quote}"</p>}
                </div>
              ))}
            </div>
            {plan.end_card?.tagline && (
              <div className="pt-2 border-t border-border">
                <p className="text-muted-foreground">End card</p>
                <p>{plan.end_card.tagline} — <span className="text-muted-foreground">{plan.end_card.cta}</span></p>
              </div>
            )}
          </div>
        </details>
      )}

      {/* Result image + save / use buttons */}
      {resultUrl && (
        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold">Storyboard ready</p>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                value={savingFolderId}
                onChange={(e) => setSavingFolderId(e.target.value)}
                disabled={resultAssetId !== null}
              >
                <option value="">No folder</option>
                {allFolders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSaveToLibrary}
                disabled={resultAssetId !== null}
              >
                <Save className="h-3.5 w-3.5" />
                {resultAssetId ? 'Saved' : 'Save to context'}
              </Button>
              <Button size="sm" onClick={handleUseInCreateAd}>
                Use in Create Ad
              </Button>
            </div>
          </div>
          <img
            src={resultUrl}
            alt="Generated storyboard"
            className="w-full rounded-md border border-border"
          />
        </div>
      )}
    </div>
  )
}

// ─── Tab value helpers ────────────────────────────────────────────────────────

// ─── Quick tab (Task 5) ───────────────────────────────────────────────────
// Prompt + optional reference images → image. Skips the Avatar/Product/
// Composite setup flows entirely; calls /api/v1/generate directly with
// output_type='image'. Save-to-library on the result lands the asset in
// the user's /Generated folder (or auto-routes via the existing scene-type
// fallback). Designed as a free-form sandbox for fast iteration; the heavier
// tabs remain for workflow-bound generations.

type QuickModelId =
  | 'google/gemini-2.5-flash-image'
  | 'google/gemini-3-pro-image-preview'
  | 'openai/gpt-5.4-image-2'

interface QuickModelOption {
  id: QuickModelId
  label: string
  blurb: string
  pricePerImageUsd: number
}

const QUICK_MODELS: readonly QuickModelOption[] = [
  {
    id: 'google/gemini-2.5-flash-image',
    label: 'Nano Banana 2',
    blurb: 'Cheapest, fastest. Good for iteration drafts.',
    pricePerImageUsd: 0.005,
  },
  {
    id: 'google/gemini-3-pro-image-preview',
    label: 'Nano Banana Pro',
    blurb: 'Higher-fidelity Gemini Pro variant. Mid-tier price.',
    pricePerImageUsd: 0.09,
  },
  {
    id: 'openai/gpt-5.4-image-2',
    label: 'GPT Image 2',
    blurb: 'Premium quality, long-prompt aware. Slowest, priciest.',
    pricePerImageUsd: 0.225,
  },
]

type QuickAspect = '1:1' | '9:16' | '3:4' | '16:9' | '4:3'

const QUICK_ASPECTS: readonly { value: QuickAspect; label: string }[] = [
  { value: '1:1', label: '1:1 Square' },
  { value: '9:16', label: '9:16 Vertical' },
  { value: '3:4', label: '3:4 Portrait' },
  { value: '16:9', label: '16:9 Wide' },
  { value: '4:3', label: '4:3 Classic' },
]

type QuickStage = 'composing' | 'generating' | 'done' | 'error'

function QuickTab({
  allAssets,
  allFolders: _allFolders,
}: {
  allAssets: Asset[]
  allFolders: AssetFolder[]
}) {
  const { toast } = useToast()
  const navigate = useNavigate()

  // ── Form state ──
  const [prompt, setPrompt] = React.useState('')
  const [modelId, setModelId] = React.useState<QuickModelId>(QUICK_MODELS[0].id)
  const [aspect, setAspect] = React.useState<QuickAspect>('1:1')
  const [numImages, setNumImages] = React.useState(1)
  const [refAssetIds, setRefAssetIds] = React.useState<number[]>([])
  const [showRefPicker, setShowRefPicker] = React.useState(false)
  // Lifts the library-picker view list to include any freshly-uploaded
  // assets that haven't shown up in the page-level `allAssets` cache yet.
  const [uploadedRefAssets, setUploadedRefAssets] = React.useState<Asset[]>([])
  const [refUploading, setRefUploading] = React.useState(false)
  const refUploadInputRef = React.useRef<HTMLInputElement>(null)

  // Upload-to-library handler — mirrors the Storyboard tab's quick-save
  // pattern. Each file is POSTed to /assets/quick-save and the returned
  // asset is auto-picked as a reference for this generation.
  const handleRefUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    const remaining = 4 - refAssetIds.length
    if (remaining <= 0) {
      toast({ title: 'Already at 4 references', description: 'Remove one to add another.' })
      e.target.value = ''
      return
    }
    setRefUploading(true)
    try {
      const accepted = files.slice(0, remaining)
      const results = await Promise.allSettled(
        accepted.map(async (file) => {
          const fd = new FormData()
          fd.append('file', file)
          const res = await apiFetch('/api/v1/assets/quick-save', { method: 'POST', body: fd })
          if (!res.ok) throw new Error(`upload failed: ${file.name}`)
          return (await res.json()) as Asset
        }),
      )
      const newAssets: Asset[] = []
      results.forEach((r) => {
        if (r.status === 'fulfilled') newAssets.push(r.value)
      })
      if (newAssets.length > 0) {
        setUploadedRefAssets((prev) => [...prev, ...newAssets])
        setRefAssetIds((prev) => [...prev, ...newAssets.map((a) => a.id)])
      }
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) {
        toast({ title: `${failed} upload${failed === 1 ? '' : 's'} failed`, variant: 'destructive' })
      }
    } finally {
      setRefUploading(false)
      if (refUploadInputRef.current) refUploadInputRef.current.value = ''
    }
  }

  // ── Generation lifecycle state ──
  const [stage, setStage] = React.useState<QuickStage>('composing')
  const [pollingJobId, setPollingJobId] = React.useState<number | null>(null)
  const [resultJobId, setResultJobId] = React.useState<number | null>(null)
  const [resultUrl, setResultUrl] = React.useState<string | null>(null)
  const [resultAssetId, setResultAssetId] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const selectedModel = React.useMemo(
    () => QUICK_MODELS.find((m) => m.id === modelId) ?? QUICK_MODELS[0],
    [modelId],
  )
  const estimatedCostCents = Math.round(
    selectedModel.pricePerImageUsd * numImages * 100,
  )

  // Resolve picked ref asset IDs to URLs the backend can fetch. Same pattern
  // as the assembly endpoint — file_path is relative to /uploads. The
  // window.location.origin assumption holds while api + uploads + frontend
  // share an origin (current Nginx setup). If a CDN or separate media
  // origin is ever introduced, switch to settings.BASE_URL via a small
  // /api/v1/config endpoint so this stays in sync with server-side ref
  // URL construction.
  const refImageUrls = React.useMemo<string[]>(() => {
    const byId = new Map(allAssets.map((a) => [a.id, a]))
    return refAssetIds
      .map((id) => byId.get(id))
      .filter((a): a is Asset => Boolean(a))
      .map((a) => {
        // file_path never carries the 'uploads/' prefix per the asset model
        // convention; the .replace is belt-and-suspenders for legacy rows.
        const rel = a.file_path?.replace(/^uploads\//, '') ?? ''
        return `${window.location.origin}/uploads/${rel}`
      })
  }, [refAssetIds, allAssets])

  // ── Generate handler ──
  // canGenerate also requires pollingJobId === null so a fast double-click on
  // "New image → Generate" can't fire two concurrent POSTs and leave an
  // orphaned poll cycle (review fix).
  const canGenerate =
    prompt.trim().length >= 3 && stage !== 'generating' && pollingJobId === null

  const handleGenerate = async () => {
    setError(null)
    setResultJobId(null)
    setResultUrl(null)
    setResultAssetId(null)
    setStage('generating')
    try {
      const body: Record<string, unknown> = {
        prompt: prompt.trim(),
        aspect_ratio: aspect,
        output_type: 'image',
        model_id: modelId,
        num_images: numImages,
      }
      if (refImageUrls.length > 0) body.image_urls = refImageUrls
      const resp = await apiJson<{ job: { id: number } }>(
        '/api/v1/generate',
        { method: 'POST', body: JSON.stringify(body) },
      )
      setPollingJobId(resp.job.id)
    } catch (err) {
      setError((err as Error).message)
      setStage('error')
    }
  }

  // ── Poll job until completed/failed ──
  React.useEffect(() => {
    if (!pollingJobId) return
    let cancelled = false
    const tick = async () => {
      try {
        const data = await apiJson<{
          id: number
          status: string
          output_url: string | null
          error_message: string | null
        }>(`/api/v1/jobs/${pollingJobId}`)
        if (cancelled) return
        if (data.status === 'completed' && data.output_url) {
          setResultUrl(data.output_url)
          setResultJobId(data.id)
          setPollingJobId(null)
          setStage('done')
        } else if (data.status === 'failed') {
          setError(data.error_message ?? 'Image render failed.')
          setPollingJobId(null)
          setStage('error')
        }
      } catch {
        /* keep polling — network flake */
      }
    }
    void tick()
    const handle = window.setInterval(tick, 4000)
    return () => {
      cancelled = true
      window.clearInterval(handle)
    }
  }, [pollingJobId])

  // ── Save-to-library ──
  const handleSave = async () => {
    if (!resultJobId) return
    try {
      const saved = await apiJson<{ asset_id: number }>(
        `/api/v1/jobs/${resultJobId}/save-to-library`,
        { method: 'POST', body: JSON.stringify({}) },
      )
      setResultAssetId(saved.asset_id)
      toast({
        title: 'Saved to library',
        description: 'Find it in Saved Context → Generated.',
      })
    } catch (err) {
      toast({
        title: 'Save failed',
        description: (err as Error).message,
        variant: 'destructive',
      })
    }
  }

  // ── Reset for a new generation ──
  const handleReset = () => {
    setStage('composing')
    setError(null)
    setPollingJobId(null)
    setResultJobId(null)
    setResultUrl(null)
    setResultAssetId(null)
  }

  // ── Ref-asset picker toggle ──
  const handleRefToggle = (asset: Asset) => {
    setRefAssetIds((prev) =>
      prev.includes(asset.id)
        ? prev.filter((id) => id !== asset.id)
        : prev.length >= 4
          ? prev
          : [...prev, asset.id],
    )
  }

  const handleRefClear = () => setRefAssetIds([])

  // Combined pool: page-level cache + assets the user just uploaded inline
  // (which won't appear in `allAssets` until the page refetches).
  const combinedAssets = React.useMemo(
    () => [...allAssets, ...uploadedRefAssets.filter((u) => !allAssets.some((a) => a.id === u.id))],
    [allAssets, uploadedRefAssets],
  )

  // Selected ref assets in display order — for the inline strip + the picker
  // disabled state. Stripped from outer scope so the picker can show counts.
  const selectedRefAssets = React.useMemo(
    () =>
      refAssetIds
        .map((id) => combinedAssets.find((a) => a.id === id))
        .filter((a): a is Asset => Boolean(a)),
    [refAssetIds, combinedAssets],
  )

  return (
    <div className="space-y-6">
      {/* ── Compose ── */}
      <div className="space-y-2">
        <Label htmlFor="quick-prompt" className="flex items-center gap-1.5">
          What do you want to create?
          <HelpHint text="Plain English. The model interprets the prompt and produces an image. Reference images, when attached, are blended in as visual anchors (composition, subject, palette). Skip the heavy setup — just describe what you want." />
        </Label>
        <Textarea
          id="quick-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A close-up product shot of an amber serum bottle on white marble, soft window light from the left, true-to-life skin tones in the background..."
          rows={4}
          className="text-sm resize-none"
        />
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>{prompt.trim().length}/4000</span>
          <span>Minimum 3 characters.</span>
        </div>
      </div>

      {/* ── Reference images (optional) ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-1.5">
            <ImageIcon className="h-3.5 w-3.5" />
            Reference images
            <Badge variant="outline" className="text-[10px] font-normal">optional</Badge>
            {refAssetIds.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground ml-1">
                · {refAssetIds.length}/4 attached
              </span>
            )}
          </Label>
          <div className="flex items-center gap-1.5">
            {refAssetIds.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRefClear}
                className="h-7 text-xs"
              >
                Clear
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowRefPicker((p) => !p)}
              className="h-7 text-xs"
            >
              {showRefPicker ? 'Hide library' : 'Pick from library'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={refUploading || refAssetIds.length >= 4}
              onClick={() => refUploadInputRef.current?.click()}
              className="h-7 text-xs"
            >
              {refUploading ? (
                <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Uploading…</>
              ) : (
                <><Upload className="h-3 w-3 mr-1" /> Upload</>
              )}
            </Button>
            <input
              ref={refUploadInputRef}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              onChange={handleRefUpload}
              disabled={refUploading}
            />
          </div>
        </div>

        {selectedRefAssets.length > 0 && (
          <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-border p-2">
            {selectedRefAssets.map((a) => (
              <div
                key={a.id}
                className="relative w-14 h-14 rounded-md overflow-hidden border border-border"
                title={a.original_filename ?? a.filename}
              >
                <img
                  src={`/uploads/${a.file_path}`}
                  alt={a.original_filename ?? a.filename}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRefToggle(a)}
                  className="absolute top-0.5 right-0.5 rounded-full bg-background/80 text-foreground p-0.5 hover:bg-background"
                  aria-label="Remove reference"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {showRefPicker && (
          <div className="rounded-md border border-border p-3 space-y-2">
            {(() => {
              const libraryImages = combinedAssets.filter((a) => a.file_type === 'image')
              if (libraryImages.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground py-2 text-center">
                    No images in your library yet. Click Upload above, or upload some from{' '}
                    <a
                      href="/saved-context"
                      className="underline hover:text-foreground"
                    >
                      Saved Context
                    </a>
                    .
                  </p>
                )
              }
              const atMax = refAssetIds.length >= 4
              return (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-72 overflow-y-auto">
                  {libraryImages.map((a) => {
                    const selected = refAssetIds.includes(a.id)
                    const disabled = !selected && atMax
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => handleRefToggle(a)}
                        disabled={disabled}
                        aria-pressed={selected}
                        className={
                          selected
                            ? 'relative aspect-square rounded overflow-hidden border-2 border-foreground ring-1 ring-foreground/50'
                            : disabled
                              ? 'relative aspect-square rounded overflow-hidden border border-border opacity-40 cursor-not-allowed'
                              : 'relative aspect-square rounded overflow-hidden border border-border hover:border-foreground/50'
                        }
                        title={
                          disabled
                            ? 'Max 4 references attached'
                            : (a.original_filename ?? a.filename)
                        }
                      >
                        <img
                          src={`/uploads/${a.file_path}`}
                          alt={a.original_filename ?? a.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {selected && (
                          <span className="absolute top-1 right-1 rounded-full bg-foreground text-background p-0.5">
                            <Check className="h-3 w-3" />
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}
      </div>

      {/* ── Aspect ratio + num images ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Aspect ratio</Label>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_ASPECTS.map((a) => {
              const active = aspect === a.value
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setAspect(a.value)}
                  className={
                    active
                      ? 'rounded-md border border-foreground bg-foreground text-background px-2.5 py-1 text-xs transition-colors'
                      : 'rounded-md border border-border hover:border-foreground/50 text-muted-foreground px-2.5 py-1 text-xs transition-colors'
                  }
                  aria-pressed={active}
                >
                  {a.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="quick-num">
            Variations
            <span className="ml-2 text-[11px] text-muted-foreground">1–4</span>
          </Label>
          <Input
            id="quick-num"
            type="number"
            min={1}
            max={4}
            value={numImages}
            onChange={(e) => {
              const n = Number(e.target.value)
              if (Number.isFinite(n)) setNumImages(Math.max(1, Math.min(4, Math.round(n))))
            }}
            className="w-24"
          />
        </div>
      </div>

      {/* ── Model picker ── */}
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5">
          Model
          <HelpHint text="Three image models. Banana 2 is the cheapest iteration model. Banana Pro is Google's higher-fidelity variant. GPT Image 2 is the most expensive but handles long, complex prompts best." />
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {QUICK_MODELS.map((m) => {
            const active = modelId === m.id
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setModelId(m.id)}
                className={
                  active
                    ? 'rounded-md border border-foreground bg-accent/30 p-3 text-left transition-colors'
                    : 'rounded-md border border-border hover:border-foreground/40 p-3 text-left transition-colors'
                }
                aria-pressed={active}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{m.label}</span>
                  <Badge variant="secondary" className="text-[10px] font-mono">
                    ${m.pricePerImageUsd.toFixed(3)}/img
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                  {m.blurb}
                </p>
              </button>
            )
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Est. cost: ${(estimatedCostCents / 100).toFixed(3)} ({numImages} ×
          ${selectedModel.pricePerImageUsd.toFixed(3)})
        </p>
      </div>

      <Separator />

      {/* ── Result / generate area ── */}
      {stage === 'composing' && (
        <Button
          type="button"
          size="lg"
          className="w-full"
          onClick={handleGenerate}
          disabled={!canGenerate}
        >
          <Sparkles className="h-4 w-4" />
          Generate image
        </Button>
      )}

      {stage === 'generating' && (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Generating… typical render: 10–40s.
        </div>
      )}

      {stage === 'error' && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-2">
          <p className="font-semibold">Generation failed</p>
          <p className="text-destructive/80">{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="h-3.5 w-3.5" />
            Try again
          </Button>
        </div>
      )}

      {stage === 'done' && resultUrl && (
        <div className="space-y-3">
          <div className="rounded-md overflow-hidden border border-border bg-muted">
            <img
              src={(() => {
                // Backend returns output_url as either an absolute URL
                // (external models) or a relative '/uploads/...' path
                // (locally-stored renders). Same normalisation pattern the
                // other tabs / videoSrc helper use — keep them in sync.
                if (!resultUrl) return ''
                if (resultUrl.startsWith('http://') || resultUrl.startsWith('https://')) return resultUrl
                if (resultUrl.startsWith('/uploads/')) return resultUrl
                if (resultUrl.startsWith('uploads/')) return '/' + resultUrl
                return '/uploads/' + resultUrl.replace(/^\/+/, '')
              })()}
              alt="Generated image"
              className="w-full h-auto block"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={resultAssetId !== null}
            >
              {resultAssetId !== null ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5" />
                  Save to library
                </>
              )}
            </Button>
            {resultAssetId !== null && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/saved-context')}
              >
                View in library
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="ml-auto"
            >
              <Plus className="h-3.5 w-3.5" />
              New image
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}


// Task 5: 'quick' is the new default first tab — prompt + optional refs →
// image, no Avatar/Product/Composite setup required.
type TabValue = 'quick' | 'avatar' | 'product' | 'composite' | 'storyboard'

function isValidTab(v: string | null): v is TabValue {
  return (
    v === 'quick' ||
    v === 'avatar' ||
    v === 'product' ||
    v === 'composite' ||
    v === 'storyboard'
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CreateImage() {
  const location = useLocation()
  const navigate = useNavigate()

  const tabFromSearch = new URLSearchParams(location.search).get('tab')
  const [activeTab, setActiveTab] = React.useState<TabValue>(
    isValidTab(tabFromSearch) ? tabFromSearch : 'quick'
  )

  const handleTabChange = (value: string) => {
    if (isValidTab(value)) {
      setActiveTab(value)
      navigate(`/create-image?tab=${value}`, { replace: true })
    }
  }

  React.useEffect(() => {
    const searchTab = new URLSearchParams(location.search).get('tab')
    if (isValidTab(searchTab) && searchTab !== activeTab) {
      setActiveTab(searchTab)
    }
  }, [location.search]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ALL assets + folders ONCE at top level ──────────────────────────

  const [allAssets, setAllAssets] = React.useState<Asset[]>([])
  const [allFolders, setAllFolders] = React.useState<AssetFolder[]>([])
  const [loadingData, setLoadingData] = React.useState(true)

  React.useEffect(() => {
    async function load() {
      setLoadingData(true)
      try {
        const [assetRes, folderRes] = await Promise.all([
          apiFetch('/api/v1/assets/search?limit=200').catch(() => null),
          apiFetch('/api/v1/folders').catch(() => null),
        ])
        if (assetRes?.ok) {
          const data = await assetRes.json()
          setAllAssets((data.items ?? data) as Asset[])
        }
        if (folderRes?.ok) {
          const data = await folderRes.json()
          setAllFolders((data.items ?? data) as AssetFolder[])
        }
      } finally {
        setLoadingData(false)
      }
    }
    void load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-4xl mx-auto w-full space-y-6 pb-16">

      <header className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Package className="h-6 w-6" aria-hidden="true" />
          Create Image
        </h1>
        <p className="text-sm text-muted-foreground">
          Generate avatars, product variations, or composites.
        </p>
      </header>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full sm:w-auto" aria-label="Image creation mode">
          {/* Task 5: Quick is the default landing — prompt + optional refs →
              image, skipping the Avatar/Product/Composite setup flows. */}
          <TabsTrigger value="quick" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Quick
          </TabsTrigger>
          <TabsTrigger value="avatar">Avatar</TabsTrigger>
          <TabsTrigger value="product">Product</TabsTrigger>
          <TabsTrigger value="composite">Composite</TabsTrigger>
          <TabsTrigger value="storyboard">Storyboard</TabsTrigger>
        </TabsList>

        {loadingData ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading assets…
          </div>
        ) : (
          <>
            <TabsContent value="quick" className="mt-6">
              <QuickTab allAssets={allAssets} allFolders={allFolders} />
            </TabsContent>

            <TabsContent value="avatar" className="mt-6">
              <AvatarTab allAssets={allAssets} allFolders={allFolders} />
            </TabsContent>

            <TabsContent value="product" className="mt-6">
              <ProductTab allAssets={allAssets} allFolders={allFolders} />
            </TabsContent>

            <TabsContent value="composite" className="mt-6">
              <CompositeTab allAssets={allAssets} allFolders={allFolders} />
            </TabsContent>

            <TabsContent value="storyboard" className="mt-6">
              <StoryboardTab allAssets={allAssets} allFolders={allFolders} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  )
}
